<template>
    <div class="channels-table" :class="{ 'channels-table--custom': isCustomCascadesMode }">
        <div v-if="!isCustomCascadesMode" class="ct-main-cascade-label">
            <span class="ct-main-cascade-label__badge">Каскад 1 ·</span>
            <span class="ct-main-cascade-label__title">Основной</span>
            <span class="ct-main-cascade-label__desc">— используется по умолчанию во всех рассылках</span>
        </div>

        <div
            v-if="isCustomCascadesMode"
            class="ct-custom-panel"
            :class="{ 'ct-custom-panel--collapsed': !customCascadesExpanded }"
        >
            <button
                type="button"
                class="ct-custom-panel__hdr"
                @click="customCascadesExpanded = !customCascadesExpanded"
            >
                <span
                    class="ct-custom-panel__chevron"
                    :class="{ 'ct-custom-panel__chevron--open': customCascadesExpanded }"
                >
                    <v-icon size="16" color="#7A839A">mdi-chevron-down</v-icon>
                </span>
                <span class="ct-custom-panel__title">Дополнительные каскады</span>
                <span class="ct-custom-panel__meta">{{ customCascades.length }} из {{ maxCustomCascades }}</span>
                <button
                    type="button"
                    class="ct-custom-panel__add"
                    title="Добавить каскад"
                    aria-label="Добавить каскад"
                    :disabled="customCascadeBusy || customCascades.length >= maxCustomCascades"
                    @click.stop="addCustomCascade"
                >
                    <v-icon size="18" color="mainNew">mdi-plus</v-icon>
                </button>
            </button>
        </div>

        <div
            class="ct-table-content-wrap"
            :class="{
                'ct-table-content-wrap--collapsible': isCustomCascadesMode,
                'ct-table-content-wrap--open': !isCustomCascadesMode || customCascadesExpanded,
            }"
            @transitionend="onCustomPanelBodyTransitionEnd"
        >
            <div class="ct-table-content-wrap__inner">
        <div
            class="ct-table-content"
            :class="{
                'ct-table-content--custom-split': useCustomSplitLayout,
                'ct-table-content--custom-balanced': isBalancedCustomCascades,
            }"
        >
        <div
            class="ct-custom-panels"
            :class="{ 'ct-custom-panels--balanced': isBalancedCustomCascades }"
        >
        <div v-if="isTableLoading && useCustomSplitLayout" class="ct-loading-overlay ct-loading-overlay--panels">
            <v-progress-circular indeterminate size="36" width="3" color="mainNew" />
            <div class="ct-loading-overlay__label">Загружаем каналы...</div>
        </div>
        <div
            class="ct-custom-panels__fixed"
            :class="{ 'ct-custom-panels__fixed--split': useCustomSplitLayout }"
        >
        <!-- Header -->
        <div class="ct-header" :class="{ 'ct-header--custom': isCustomCascadesMode }">
            <div class="ct-header__rail" aria-hidden="true"></div>
            <div class="ct-header__cell ct-header__cell--channels">
                <span>Каналы</span>
            </div>
            <div class="ct-header__cell ct-header__cell--status">
                <span>Статус</span>
            </div>
            <template v-if="isCustomCascadesMode && !useCustomSplitLayout">
                <div
                    v-for="cascade in customCascades"
                    :key="`custom-header-${cascade.id}`"
                    class="ct-header__cell ct-header__cell--priority ct-header__cell--custom-cascade"
                >
                    <div class="ct-custom-cascade-head">
                        <span class="ct-custom-cascade-head__num">Каскад {{ getCustomCascadeDisplayNumber(cascade) }}</span>
                        <div
                            class="ct-custom-cascade-head__name-row"
                            :class="{ 'ct-custom-cascade-head__name-row--saving': isCustomCascadeNameSaving(cascade) }"
                        >
                            <span
                                :id="`casc-name-${cascade.id}`"
                                class="ct-custom-cascade-head__name"
                                :class="{ 'ct-custom-cascade-head__name--saving': isCustomCascadeNameSaving(cascade) }"
                                :title="isCustomCascadeNameSaving(cascade) ? 'Сохранение...' : getCustomCascadeDisplayName(cascade)"
                                :contenteditable="customCascadeEditingId === cascade.id && !isCustomCascadeNameSaving(cascade) ? 'true' : 'false'"
                                @click.stop="startCustomCascadeRename(cascade)"
                                @blur="finishCustomCascadeRename(cascade, $event)"
                                @keydown.stop="handleCustomCascadeRenameKeydown(cascade, $event)"
                                @input.stop="handleCustomCascadeRenameInput(cascade, $event)"
                                @paste.stop="handleCustomCascadeRenamePaste(cascade, $event)"
                            ></span>
                            <v-progress-circular
                                v-if="isCustomCascadeNameSaving(cascade)"
                                class="ct-custom-cascade-head__save-spinner"
                                indeterminate
                                size="12"
                                width="2"
                                color="mainNew"
                            />
                            <button
                                v-else
                                type="button"
                                class="ct-custom-cascade-head__btn ct-custom-cascade-head__btn--rename"
                                title="Переименовать"
                                :disabled="customCascadeBusy"
                                @click.stop="startCustomCascadeRename(cascade)"
                            >
                                <v-icon size="14">mdi-pencil-outline</v-icon>
                            </button>
                            <button
                                type="button"
                                class="ct-custom-cascade-head__btn ct-custom-cascade-head__btn--danger"
                                title="Удалить каскад"
                                :disabled="customCascadeBusy || isCustomCascadeNameSaving(cascade) || customCascades.length <= 1"
                                @click.stop="removeCustomCascade(cascade)"
                            >
                                <v-icon size="14">mdi-delete-outline</v-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </template>
            <div v-if="showPriorityColumns" class="ct-header__cell ct-header__cell--priority">
                <span><strong>МАРКЕТИНГОВЫЕ</strong><span class="ct-header__cell-sub">рассылки</span></span>
                <v-tooltip bottom color="#000000DE" max-width="400px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ct-hint-icon" v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
                    </template>
                    <div class="white--text">
                        Укажите порядок каналов (1 → 2 → 3) для Маркетинговых рассылок по базе.
                        Если первый канал недоступен, система использует следующий.<br><br>
                        Сообщение отправляется по каналам по очереди в течение суток,
                        чтобы доставка была аккуратной и не раздражала клиентов.
                    </div>
                </v-tooltip>
            </div>
            <div v-if="showNotificationsColumn" class="ct-header__cell ct-header__cell--priority">
                <span><strong>СЕРВИСНЫЕ</strong><span class="ct-header__cell-sub">уведомления</span></span>
                <v-tooltip bottom color="#000000DE" max-width="400px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ct-hint-icon" v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
                    </template>
                    <div class="white--text">
                        Укажите порядок каналов (1 → 2 → 3) для сервисных уведомлений.
                        Если первый не сработает, используем следующий.<br><br>
                        За один час сообщение отправится по всем каналам по очереди, пока не доставится.
                    </div>
                </v-tooltip>
            </div>
            <div v-if="showCrmMessagesColumn" class="ct-header__cell ct-header__cell--priority">
                <span><strong>YCLIENTS</strong><span class="ct-header__cell-sub">коды подтверждения</span></span>
                <v-tooltip bottom color="#000000DE" max-width="400px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon small class="ct-hint-icon" v-bind="attrs" v-on="on">mdi-help-circle</v-icon>
                    </template>
                    <span class="white--text">
                        Установите приоритет отправки для сообщений из YCLIENTS (коды подтверждения)
                    </span>
                </v-tooltip>
            </div>
        </div>

        <div class="ct-body">
            <div v-if="isTableLoading && !useCustomSplitLayout" class="ct-loading-overlay">
                <v-progress-circular indeterminate size="36" width="3" color="mainNew" />
                <div class="ct-loading-overlay__label">Загружаем каналы...</div>
            </div>

            <!-- Groups -->
            <div v-for="group in groups" :key="group.type" class="ct-group">
                <div class="ct-group__rail" :class="'ct-group__rail--' + group.type">
                    <div class="ct-group__rail-inner">
                        <span class="ct-group__rail-badge" :class="'ct-group__rail-badge--' + group.type">
                            {{ group.label }}
                        </span>
                    </div>
                </div>
                <div class="ct-group__rows" :class="'ct-group__rows--' + group.type">
                    <div
                        v-for="item in group.items"
                        :key="getItemKey(item)"
                        class="ct-row"
                        :class="{
                            'ct-row--clickable': !item._isPendingPayment && !item.isNoTariff && !item.isTariffEmpty && (canOpenChannelSettings(item) || canFinishConnectorCreate(item) || item.isActive === false),
                            'ct-row--frozen': !item.isTariffEmpty && shouldShowFrozenStatus(item),
                            'ct-row--pending': !!item._isPendingPayment,
                            'ct-row--max-slot': isMaxBusinessSlot(item),
                            'ct-row--custom': isCustomCascadesMode,
                        }"
                        @click="!item._isPendingPayment && !item.isNoTariff && !item.isTariffEmpty && handleRowClick(item)"
                    >
                    <div
                        v-if="isMaxBusinessSlot(item) && !item.isNoTariff && !item.isTariffEmpty"
                        class="ct-max-slot-crown-anchor"
                    >
                        <v-tooltip
                            bottom
                            color="#000000DE"
                            max-width="220px"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <div
                                    class="ct-max-slot-crown"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click.stop
                                >
                                    <v-icon size="12" color="#FFFFFF">mdi-crown</v-icon>
                                </div>
                            </template>
                            <span class="white--text">MAX Business</span>
                        </v-tooltip>
                    </div>
                    <!-- No-tariff placeholder row -->
                    <template v-if="item.isNoTariff">
                        <div class="ct-cell ct-cell--channel ct-cell--no-tariff">
                            <v-icon color="#AAB1B9" size="22">mdi-credit-card-off-outline</v-icon>
                            <div class="ct-inactive-text">
                                <span class="ct-link" @click.stop="openNoTariffBillingPage">{{ noTariffLinkText }}</span>,<br>
                                <span class="ct-no-tariff-subline">чтобы подключить каналы</span>
                            </div>
                        </div>
                        <div class="ct-cell ct-cell--auth"></div>
                        <div v-if="showPriorityColumns" class="ct-cell ct-cell--priority"></div>
                        <div v-if="showNotificationsColumn" class="ct-cell ct-cell--priority"></div>
                        <div v-if="showCrmMessagesColumn" class="ct-cell ct-cell--priority"></div>
                        <template v-if="isCustomCascadesMode && !useCustomSplitLayout">
                            <div
                                v-for="cascade in customCascades"
                                :key="`no-tariff-custom-${cascade.id}`"
                                class="ct-cell ct-cell--priority"
                            ></div>
                        </template>
                    </template>
                    <template v-else-if="item.isTariffEmpty">
                        <div class="ct-cell ct-cell--channel ct-cell--tariff-empty">
                            <v-icon color="#AAB1B9" size="22">mdi-information-outline</v-icon>
                            <div class="ct-inactive-text">В тарифе пока нет каналов</div>
                        </div>
                        <div class="ct-cell ct-cell--auth"></div>
                        <div v-if="showPriorityColumns" class="ct-cell ct-cell--priority"></div>
                        <div v-if="showNotificationsColumn" class="ct-cell ct-cell--priority"></div>
                        <div v-if="showCrmMessagesColumn" class="ct-cell ct-cell--priority"></div>
                        <template v-if="isCustomCascadesMode && !useCustomSplitLayout">
                            <div
                                v-for="cascade in customCascades"
                                :key="`tariff-empty-custom-${cascade.id}`"
                                class="ct-cell ct-cell--priority"
                            ></div>
                        </template>
                    </template>

                    <!-- Channel info cell -->
                    <div
                        v-if="!item.isNoTariff && !item.isTariffEmpty"
                        class="ct-cell ct-cell--channel"
                    >
                        <template v-if="item.isActive !== false">
                            <v-avatar
                                :color="getConnectorAvatar(item).color"
                                size="36"
                                class="ct-avatar"
                                :class="{ 'ct-avatar--inactive': item.isActive === false }"
                            >
                                <v-img
                                    v-if="getConnectorAvatar(item).image"
                                    transition="slide-x-transition"
                                    :src="getConnectorAvatar(item).image"
                                    :class="{ 'ct-img--inactive': item.isActive === false }"
                                />
                                <span v-else>{{ getConnectorAvatar(item).symbol }}</span>
                            </v-avatar>
                            <div class="ct-channel-info">
                                <div class="ct-channel-name-row">
                                    <div class="ct-channel-name">
                                        {{ getConnectorAvatar(item).name }}
                                        <span v-if="item.isDraft"> (Draft)</span>
                                    </div>
                                    <v-tooltip
                                        v-if="!isAuthStatusLoading(item) && !isChannelAuthorized(item) && shouldShowQrCodeButton(item)"
                                        bottom
                                        color="#000000DE"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                icon
                                                small
                                                class="ct-qr-btn"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.stop="shareWhatsappBasisQrCode(item)"
                                            >
                                                <v-icon size="22">mdi-qrcode-scan</v-icon>
                                            </v-btn>
                                        </template>
                                        <span class="white--text">Поделиться QR-кодом</span>
                                    </v-tooltip>
                                </div>
                                <div v-if="getConnectorPhone(item)" class="ct-channel-phone">{{ getConnectorPhone(item) }}</div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="ct-inactive-icon" :class="{ 'ct-inactive-icon--pending': item._isPendingPayment }">
                                <span>+</span>
                            </div>
                            <div class="ct-inactive-text">
                                <v-tooltip
                                    v-if="item._isPendingPayment"
                                    bottom
                                    color="#000000DE"
                                    max-width="190px"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <span
                                            class="ct-inactive-text-pending-activator"
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <template v-if="isMaxBusinessSlot(item)">
                                                <span class="ct-inactive-text--pending">Подключите</span> канал
                                            </template>
                                            <template v-else>
                                                <span class="ct-inactive-text--pending">Подключите</span> или
                                                <br>
                                                <span class="ct-inactive-text--pending">перенесите</span> канал
                                            </template>
                                        </span>
                                    </template>
                                    <span class="white--text">Станет доступно после подтверждения оплаты</span>
                                </v-tooltip>
                                <template v-else-if="isMaxBusinessSlot(item)">
                                    <span
                                        class="ct-link"
                                        @click.stop="connectInactiveChannel(item, 'new')"
                                    >
                                        Подключите
                                    </span> канал
                                </template>
                                <template v-else>
                                    <span
                                        class="ct-link"
                                        @click.stop="connectInactiveChannel(item, 'new')"
                                    >
                                        Подключите
                                    </span> или
                                    <br>
                                    <span
                                        class="ct-link"
                                        @click.stop="connectInactiveChannel(item, 'transfer')"
                                    >
                                        перенесите
                                    </span> канал
                                </template>
                            </div>
                        </template>
                    </div>

                    <!-- Auth status cell -->
                    <div v-if="!item.isNoTariff && !item.isTariffEmpty" class="ct-cell ct-cell--auth" @click.stop="handleAuthCellClick(item)">
                        <template v-if="item._isPendingPayment">
                            <div class="ct-auth-col">
                                <div class="ct-auth-status-pending-row">
                                    <div class="ct-auth-status ct-auth-status--pending">
                                        <ClockIcon :style="{ color: '#7F8997', width: '14px', height: '14px' }" />
                                        <span class="ct-auth-label ct-auth-label--pending">Оплата в обработке</span>
                                    </div>
                                    <v-btn
                                        icon
                                        x-small
                                        :disabled="slotsSilentRefreshing"
                                        aria-label="Обновить статусы оплаты"
                                        @click.stop="handleRefreshPendingSlots"
                                    >
                                        <SpinArrows
                                            class="ct-auth-refresh-icon"
                                            :class="{ 'ct-auth-refresh-icon--spinning': slotsSilentRefreshing }"
                                        />
                                    </v-btn>
                                </div>
                                <div class="ct-auth-subtext ct-auth-subtext--pending">
                                    {{ getPendingPaymentSubtext(item) }}
                                </div>
                            </div>
                        </template>
                        <template v-else-if="shouldShowFrozenStatus(item)">
                            <div class="ct-auth-col">
                                <div class="ct-auth-status ct-auth-status--frozen">
                                    <v-icon size="16" color="#FD9C2B">mdi-minus-circle</v-icon>
                                    <span class="ct-auth-label ct-auth-label--frozen">Заморожен</span>
                                </div>
                                <div class="ct-auth-subtext ct-auth-subtext--frozen">
                                    {{ frozenStatusSubtext(item) }}
                                </div>
                            </div>
                        </template>
                        <template v-else-if="item.isActive !== false && !isHrTelegram(item)">
                            <div class="ct-auth-col">
                                <div
                                    :class="['ct-auth-status', { 'ct-auth-status--error': !isAuthStatusLoading(item) && !isChannelAuthorized(item) }]"
                                    :style="{ cursor: canHandleAuthCellClick(item) ? 'pointer' : 'default' }"
                                >
                                    <v-progress-circular
                                        v-if="isAuthStatusLoading(item)"
                                        indeterminate
                                        size="18"
                                        width="2"
                                        color="mainNew"
                                    />
                                    <img
                                        v-else-if="isChannelAuthorized(item)"
                                        class="ct-auth-icon ct-auth-icon--ok"
                                        :src="require('@/assets/icons/success-status.svg')"
                                    />
                                    <img
                                        v-else
                                        class="ct-auth-icon"
                                        :src="require('@/assets/icons/error-status.svg')"
                                    />
                                    <span v-if="isAuthStatusLoading(item)" class="ct-auth-label">
                                        Проверка...
                                    </span>
                                    <span v-else-if="isChannelAuthorized(item)" class="ct-auth-label ct-auth-label--ok">
                                        Подключен
                                    </span>
                                    <span v-else class="ct-auth-label ct-auth-label--error">
                                        Не подключен
                                    </span>
                                </div>
                                <div
                                    v-if="!isAuthStatusLoading(item) && !isChannelAuthorized(item)"
                                    class="ct-auth-subtext"
                                    style="padding: 0 0 0 22px"
                                >
                                    Требуется переподключить
                                </div>
                                <!-- SMS: канал привязан, но телефон не выходит на связь.
                                     Привязка и связь — разное, поэтому подпись, а не смена статуса. -->
                                <div
                                    v-else-if="isSmsChannelOffline(item)"
                                    class="ct-auth-subtext ct-auth-subtext--offline"
                                    style="padding: 0 0 0 22px"
                                >
                                    Телефон не в сети
                                </div>
                            </div>
                        </template>
                        <template v-else-if="item.isActive === false && checkRole(projectId, 'project:connector:edit')">
                            <div class="ct-auth-col">
                                <div class="ct-auth-status ct-auth-status--available">
                                    <v-icon small color="mainNew">mdi-plus-circle</v-icon>
                                    <span class="ct-auth-label ct-auth-label--available">Доступно подключение</span>
                                </div>
                            </div>
                        </template>
                    </div>

                    <!-- Mailings priority cell -->
                    <div v-if="!item.isNoTariff && !item.isTariffEmpty && showPriorityColumns" class="ct-cell ct-cell--priority">
                        <PrioritySelect
                            v-if="item.isActive !== false && isChannelType(item)"
                            :value="getMailingsPriorityForWhatsappDh(item)"
                            :max-priority="getMaxPriorityForType('mailings')"
                            :excluded-priorities="getExcludedPriorities(item.connectorId || item.id, 'mailings')"
                            :disabled="isWhatsappDhChannel(item) && isMailingsPriorityLocked(item)"
                            :tooltip="getMailingsPriorityTooltip(item)"
                            @input="handleMailingsPriorityInput(item, $event)"
                        />
                        <div v-else-if="item.isActive === false" class="ct-priority-disabled">
                            <span class="ct-priority-disabled__label">Приоритет</span>
                            <span class="ct-priority-disabled__box">✕</span>
                        </div>
                    </div>

                    <!-- Notifications priority cell -->
                    <div v-if="!item.isNoTariff && !item.isTariffEmpty && showNotificationsColumn" class="ct-cell ct-cell--priority">
                        <PrioritySelect
                            v-if="item.isActive !== false && isChannelType(item)"
                            :value="getNotificationsPriorityForWhatsappDh ? getNotificationsPriorityForWhatsappDh(item) : getChannelPriority(item.connectorId || item.id, 'notifications')"
                            :max-priority="getMaxPriorityForType('notifications')"
                            :excluded-priorities="getExcludedPriorities(item.connectorId || item.id, 'notifications')"
                            :disabled="isWhatsappDhChannel(item)"
                            :tooltip="getNotificationsPriorityTooltip ? getNotificationsPriorityTooltip(item) : ''"
                            @input="updateChannelPriority(item.connectorId || item.id, 'notifications', $event)"
                        />
                        <div v-else-if="item.isActive === false" class="ct-priority-disabled">
                            <span class="ct-priority-disabled__label">Приоритет</span>
                            <span class="ct-priority-disabled__box">✕</span>
                        </div>
                    </div>

                    <!-- CRM Messages priority cell -->
                    <div v-if="!item.isNoTariff && !item.isTariffEmpty && showCrmMessagesColumn" class="ct-cell ct-cell--priority">
                        <PrioritySelect
                            v-if="item.isActive !== false && isChannelType(item)"
                            :value="getCrmMessagesPriorityForWhatsappDh(item)"
                            :max-priority="getMaxPriorityForType('crmMessages')"
                            :excluded-priorities="getExcludedPriorities(item.connectorId || item.id, 'crmMessages')"
                            :disabled="isWhatsappDhChannel(item)"
                            :tooltip="getCrmMessagesPriorityTooltip(item)"
                            @input="updateChannelPriority(item.connectorId || item.id, 'crmMessages', $event)"
                        />
                        <div v-else-if="item.isActive === false" class="ct-priority-disabled">
                            <span class="ct-priority-disabled__label">Приоритет</span>
                            <span class="ct-priority-disabled__box">✕</span>
                        </div>
                    </div>

                    <template v-if="!item.isNoTariff && !item.isTariffEmpty && isCustomCascadesMode && !useCustomSplitLayout">
                        <div
                            v-for="cascade in customCascades"
                            :key="`custom-priority-${cascade.id}-${getItemKey(item)}`"
                            class="ct-cell ct-cell--priority"
                        >
                            <PrioritySelect
                                v-if="item.isActive !== false && isChannelType(item)"
                                :value="getCustomCascadePriority(item, cascade)"
                                :max-priority="getMaxPriorityForCustomCascade(cascade)"
                                :excluded-priorities="getExcludedPrioritiesForCustomCascade(item, cascade)"
                                :disabled="isWhatsappDhChannel(item) || customCascadeBusy"
                                :tooltip="getCustomCascadePriorityTooltip(item)"
                                @input="handleCustomCascadePriorityInput(item, cascade, $event)"
                            />
                            <div v-else-if="item.isActive === false" class="ct-priority-disabled">
                                <span class="ct-priority-disabled__label">Приоритет</span>
                                <span class="ct-priority-disabled__box">✕</span>
                            </div>
                        </div>
                    </template>
                    </div>
                </div>
            </div>

            <div
                v-if="!useCustomSplitLayout"
                class="ct-buy-strip"
            >
                <div class="ct-buy-strip__rail" aria-hidden="true"></div>
                <div class="ct-buy-strip__content">
                    <div class="ct-buy-strip__left">
                        <span class="ct-buy-strip__icon" aria-hidden="true">+</span>
                        <div class="ct-buy-strip__copy">
                            <div
                                class="ct-buy-strip__title"
                                :title="buyStripTitle"
                            >
                                {{ buyStripTitle }}
                            </div>
                            <div class="ct-buy-strip__sub">
                                {{
                                    showUpperPromoBlock
                                        ? 'Доп. канал: WhatsApp, Telegram или MAX · 990 ₽/мес · Тариф: 3 канала вместо одного, аналитика, KPI и безлимитные активации'
                                        : 'WhatsApp, Telegram или MAX · 990 ₽/мес'
                                }}
                            </div>
                        </div>
                    </div>
                    <div class="ct-buy-strip__actions">
                        <button
                            v-if="showUpperPromoBlock"
                            type="button"
                            class="ct-buy-strip__btn"
                            @click="onUpgradeTariffClick"
                        >
                            Повысить тариф
                        </button>
                        <button
                            type="button"
                            class="ct-buy-strip__btn"
                            @click="onBuyAdditionalChannelClick"
                        >
                            Купить
                        </button>
                    </div>
                </div>
            </div>

        </div>

            <div
                v-if="useCustomSplitLayout"
                class="ct-buy-strip-part ct-buy-strip-part--fixed"
            >
                <div class="ct-buy-strip__rail" aria-hidden="true"></div>
                <div class="ct-buy-strip__content ct-buy-strip__content--fixed">
                    <div class="ct-buy-strip__left">
                        <span class="ct-buy-strip__icon" aria-hidden="true">+</span>
                        <div class="ct-buy-strip__copy">
                            <div
                                class="ct-buy-strip__title"
                                :title="buyStripTitle"
                            >
                                {{ buyStripTitle }}
                            </div>
                            <div class="ct-buy-strip__sub">
                                {{
                                    showUpperPromoBlock
                                        ? 'Доп. канал: WhatsApp, Telegram или MAX · 990 ₽/мес · Тариф: 3 канала вместо одного, аналитика, KPI и безлимитные активации'
                                        : 'WhatsApp, Telegram или MAX · 990 ₽/мес'
                                }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <div v-if="useCustomSplitLayout" class="ct-custom-panels__scroll-scroller">
            <div class="ct-custom-panels__scroll-wide">
            <div class="ct-header ct-header--custom-scroll">
                <div
                    v-if="showCustomCascadeEmptyColumn"
                    class="ct-header__cell ct-header__cell--priority ct-header__cell--custom-cascade ct-header__cell--custom-cascade-empty"
                    aria-hidden="true"
                ></div>
                <div
                    v-for="cascade in customCascades"
                    :key="`custom-header-scroll-${cascade.id}`"
                    class="ct-header__cell ct-header__cell--priority ct-header__cell--custom-cascade"
                >
                    <div class="ct-custom-cascade-head">
                        <span class="ct-custom-cascade-head__num">Каскад {{ getCustomCascadeDisplayNumber(cascade) }}</span>
                        <div
                            class="ct-custom-cascade-head__name-row"
                            :class="{ 'ct-custom-cascade-head__name-row--saving': isCustomCascadeNameSaving(cascade) }"
                        >
                            <span
                                :id="`casc-name-${cascade.id}`"
                                class="ct-custom-cascade-head__name"
                                :class="{ 'ct-custom-cascade-head__name--saving': isCustomCascadeNameSaving(cascade) }"
                                :title="isCustomCascadeNameSaving(cascade) ? 'Сохранение...' : getCustomCascadeDisplayName(cascade)"
                                :contenteditable="customCascadeEditingId === cascade.id && !isCustomCascadeNameSaving(cascade) ? 'true' : 'false'"
                                @click.stop="startCustomCascadeRename(cascade)"
                                @blur="finishCustomCascadeRename(cascade, $event)"
                                @keydown.stop="handleCustomCascadeRenameKeydown(cascade, $event)"
                                @input.stop="handleCustomCascadeRenameInput(cascade, $event)"
                                @paste.stop="handleCustomCascadeRenamePaste(cascade, $event)"
                            ></span>
                            <v-progress-circular
                                v-if="isCustomCascadeNameSaving(cascade)"
                                class="ct-custom-cascade-head__save-spinner"
                                indeterminate
                                size="12"
                                width="2"
                                color="mainNew"
                            />
                            <button
                                v-else
                                type="button"
                                class="ct-custom-cascade-head__btn ct-custom-cascade-head__btn--rename"
                                title="Переименовать"
                                :disabled="customCascadeBusy"
                                @click.stop="startCustomCascadeRename(cascade)"
                            >
                                <v-icon size="14">mdi-pencil-outline</v-icon>
                            </button>
                            <button
                                type="button"
                                class="ct-custom-cascade-head__btn ct-custom-cascade-head__btn--danger"
                                title="Удалить каскад"
                                :disabled="customCascadeBusy || isCustomCascadeNameSaving(cascade) || customCascades.length <= 1"
                                @click.stop="removeCustomCascade(cascade)"
                            >
                                <v-icon size="14">mdi-delete-outline</v-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div
                class="ct-body ct-body--custom-scroll"
                :class="{
                    'ct-body--custom-scroll-empty': showCustomCascadeEmptyColumn,
                    'ct-body--custom-scroll-placeholder': showCustomCascadePlaceholder,
                }"
            >
                <div v-if="showCustomCascadePlaceholder" class="ct-custom-empty-column">
                    Дополнительные каскады пока не созданы. Нажмите «+», чтобы добавить первый.
                </div>
                <div
                    v-for="group in groups"
                    :key="`scroll-${group.type}`"
                    class="ct-group ct-group--scroll"
                >
                    <div class="ct-group__rows" :class="'ct-group__rows--' + group.type">
                        <div
                            v-for="item in group.items"
                            :key="`scroll-${getItemKey(item)}`"
                            class="ct-row ct-row--custom ct-row--scroll"
                            :class="getCustomScrollRowClasses(item)"
                        >
                            <template v-if="item.isNoTariff || item.isTariffEmpty">
                                <template v-if="showCustomCascadeEmptyColumn">
                                    <div
                                        class="ct-cell ct-cell--priority ct-cell--custom-cascade ct-cell--custom-cascade-empty"
                                        aria-hidden="true"
                                    ></div>
                                </template>
                                <template v-else>
                                    <div
                                        v-for="cascade in customCascades"
                                        :key="`scroll-empty-${cascade.id}-${getItemKey(item)}`"
                                        class="ct-cell ct-cell--priority ct-cell--custom-cascade"
                                    ></div>
                                </template>
                            </template>
                            <template v-else>
                                <template v-if="showCustomCascadeEmptyColumn">
                                    <div
                                        class="ct-cell ct-cell--priority ct-cell--custom-cascade ct-cell--custom-cascade-empty"
                                        aria-hidden="true"
                                    ></div>
                                </template>
                                <template v-else>
                                    <div
                                        v-for="cascade in customCascades"
                                        :key="`scroll-priority-${cascade.id}-${getItemKey(item)}`"
                                        class="ct-cell ct-cell--priority ct-cell--custom-cascade"
                                    >
                                        <PrioritySelect
                                            v-if="item.isActive !== false && isChannelType(item)"
                                            :value="getCustomCascadePriority(item, cascade)"
                                            :max-priority="getMaxPriorityForCustomCascade(cascade)"
                                            :excluded-priorities="getExcludedPrioritiesForCustomCascade(item, cascade)"
                                            :disabled="isWhatsappDhChannel(item) || customCascadeBusy"
                                            :tooltip="getCustomCascadePriorityTooltip(item)"
                                            @input="handleCustomCascadePriorityInput(item, cascade, $event)"
                                        />
                                    <div v-else-if="item.isActive === false" class="ct-priority-disabled">
                                        <span class="ct-priority-disabled__label">Приоритет</span>
                                        <span class="ct-priority-disabled__box">✕</span>
                                    </div>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            </div>

            <div
                class="ct-buy-strip-part ct-buy-strip-part--scroll"
            >
                <div class="ct-buy-strip__content ct-buy-strip__content--scroll">
                    <div class="ct-buy-strip__actions">
                        <button
                            v-if="showUpperPromoBlock"
                            type="button"
                            class="ct-buy-strip__btn"
                            @click="onUpgradeTariffClick"
                        >
                            Повысить тариф
                        </button>
                        <button
                            type="button"
                            class="ct-buy-strip__btn"
                            @click="onBuyAdditionalChannelClick"
                        >
                            Купить
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </div>

        </div>
        </div>
        </div>

        <transition name="ct-delete-fade">
            <div
                v-if="customCascadeDeleteModalOpen"
                class="ct-delete-overlay"
                @click.self="closeCustomCascadeDeleteModal"
            >
                <div class="ct-delete-modal" role="dialog" aria-modal="true" aria-labelledby="ct-delete-modal-title">
                    <div class="ct-delete-modal__title" id="ct-delete-modal-title">Удалить каскад?</div>
                    <div class="ct-delete-modal__body">
                        Каскад <strong>"{{ customCascadePendingDeleteName }}"</strong> будет удалён.<br>
                        Все отправки, которые использовали этот каскад, будут использовать
                        <strong>канал отправки по умолчанию</strong>.
                    </div>
                    <div class="ct-delete-modal__actions">
                        <button
                            type="button"
                            class="ct-delete-modal__btn ct-delete-modal__btn--ghost"
                            :disabled="customCascadeBusy"
                            @click="closeCustomCascadeDeleteModal"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            class="ct-delete-modal__btn ct-delete-modal__btn--danger"
                            :disabled="customCascadeBusy"
                            @click="confirmRemoveCustomCascade"
                        >
                            {{ customCascadeBusy ? 'Удаление...' : 'Удалить' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <MaxMarketingRiskModal
            :value="maxRiskModalOpen"
            :context="maxRiskContext"
            @accept="acceptMaxRisk"
            @remove="removeMaxFromMailings"
            @dismiss="dismissMaxRisk"
        />
    </div>
</template>

<script>
import { checkAccess } from '@/utils'
import PrioritySelect from '@/components/project/PrioritySelect.vue'
import MaxMarketingRiskModal from '@/components/project/MaxMarketingRiskModal.vue'
import { isRegularMaxChannel } from '@/helpers/channels/isRegularMaxChannel'
import ClockIcon from '@/assets/vuetifyIcons/clock.vue'
import SpinArrows from '@/assets/vuetifyIcons/spinArrows.vue'
import { checkRole } from '@root/src/helpers/roles'
import { CONNECTOR_TYPES, PROVIDER_TYPES, CHANNEL_TYPES, PROVIDER_TO_CHANNEL_MAP } from '@root/src/vars/general'
import cfg from '../../../config'
import api from '@root/src/api/api'
import { useBillingStore } from '@/store/stores/billing'
import { fetchConnector } from '@/api/projects'
import {
    getCascadesByProject,
    createCascade,
    updateCascade,
    deleteCascade,
    updateCascadeChannelPriority,
    updateCascadeChannelsPriorities,
    deleteCascadeChannel,
    getCascadeById,
    createChannel,
    getSlots,
    getPendingSlots,
} from '@/api/networkFixer'
import { openChannelCreationSidebar } from '@/helpers/channels/openChannelCreationSidebar'
import {
    getAuthStatusConnectorId as getHelperAuthStatusConnectorId,
    isAuthStatusLoading as getIsAuthStatusLoading,
    isChannelAuthorized as getIsChannelAuthorized,
    shouldShowQrCodeButton as getShouldShowQrCodeButton,
    shouldUseLiveAuthStatus as getShouldUseLiveAuthStatus,
    shouldUseMaxBusinessAuthStatus as getShouldUseMaxBusinessAuthStatus,
    shouldUseSmsGateAuthStatus as getShouldUseSmsGateAuthStatus,
} from '@/helpers/channels/channelAuthStatus'
import { isMaxBusinessStatusAuthorized } from '@/helpers/channels/maxBusinessStatus'
import { getSmsGateOnline, isSmsGateStatusAuthorized } from '@/helpers/channels/smsGateStatus'

export default {
    name: 'ChannelsTable',
    components: { PrioritySelect, ClockIcon, SpinArrows, MaxMarketingRiskModal },
    props: {
        variant: {
            type: String,
            default: 'default',
            validator: value => ['default', 'custom-cascades'].includes(value),
        },
        connectors: {
            type: Array,
            default: () => [],
        },
        allChannelsCards: {
            type: Array,
            required: true,
        },
        channelPriorities: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        whatsappBasisNames: {},
        whatsappBasisConnectorId: null,
        whatsappBasisChannelType: null,
        whatsappBasisAuthStatus: null,
        whatsappBasisAuthInterval: null,
        // online для sms-gate: отдельно от привязки (data:true из getSmsGateStatus)
        smsOnlineByConnectorId: {},
        channelAuthStatusByConnectorId: {},
        channelAuthStatusLoadingByConnectorId: {},
        channelAuthStatusReloadQueuedByConnectorId: {},
        channelAuthStatusReloadTimerByConnectorId: {},
        isLoadingSlots: false,
        isInitialTableLoading: true,
        localChannelPriorities: {},
        savePriorityTimer: null,
        isLoadingPriorities: false,
        cascades: {},
        cascadeIds: {},
        hasAnyCascadesOnProject: null,
        hasLoadedCascades: false,
        channelSlots: [],
        pendingSlots: [],
        slotsSilentRefreshing: false,
        customCascades: [],
        customCascadesExpanded: false,
        customCascadeBusy: false,
        customCascadeDeleteModalOpen: false,
        maxRiskModalOpen: false,
        maxRiskPending: null,
        maxRiskContext: '',
        customCascadePendingDelete: null,
        maxCustomCascades: 10,
        maxCustomCascadeNameLength: 30,
        isLoadingCustomCascades: false,
        customPanelHeightSyncTimer: null,
        customPanelResizeObserver: null,
        customCascadeEditingId: null,
        customCascadeRenameOriginalName: '',
        customCascadeSavingNameId: null,
        smartBotTariffId: 'tariff2',
        maxSubscriptionPeriodMonths: 12,
    }),
    computed: {
        isCustomCascadesMode() {
            return this.variant === 'custom-cascades'
        },
        isBalancedCustomCascades() {
            return this.isCustomCascadesMode && this.customCascadeColumnCount > 0 && this.customCascadeColumnCount <= 3
        },
        useCustomSplitLayout() {
            return this.isCustomCascadesMode
        },
        showCustomCascadeEmptyColumn() {
            return this.isCustomCascadesMode && !this.customCascades.length
        },
        customCascadeColumnCount() {
            if (!this.isCustomCascadesMode) return 0
            return this.customCascades.length || 1
        },
        showCustomCascadePlaceholder() {
            return this.showCustomCascadeEmptyColumn && !this.isTableLoading
        },
        customCascadePendingDeleteName() {
            const cascade = this.customCascadePendingDelete
            if (!cascade) return ''
            return cascade.name || `Каскад ${cascade.id}`
        },
        hasBotConnector() {
            const connectors = this.project?.connectors || []
            return Boolean(connectors.some((con) => con.connectorType === CONNECTOR_TYPES.whatsappBasis))
        },
        hasYClientsAndBotConnector() {
            const connectors = this.project?.connectors || []
            const hasYClients = connectors.some((con) => con.connectorType === CONNECTOR_TYPES.yClients)
            const hasBot = connectors.some((con) => con.connectorType === CONNECTOR_TYPES.whatsappBasis)
            return Boolean(hasYClients && hasBot)
        },
        connectorsAvatars() {
            return this.$store.state.connectorsAvatars
        },
        projectId() {
            return this.$router.currentRoute.params.id
        },
        project() {
            return this.$store.getters.projectById(this.projectId)
        },
        showUpperPromoBlock() {
            if (!this.project?.billingEnabled) return false
            const tier = this.getTariffTier()
            return tier === 0 || tier === 1
        },
        buyStripTitle() {
            return this.showUpperPromoBlock
                ? 'Купить дополнительный канал или повысить тариф'
                : 'Купить дополнительный канал'
        },
        isTester() {
            return cfg.testerEmails.includes(this.$auth.user.email)
        },
        showPriorityColumns() {
            if (this.isCustomCascadesMode) return false
            return this.hasLoadedCascades && this.hasCascadeType('mailings')
        },
        showNotificationsColumn() {
            if (this.isCustomCascadesMode) return false
            return this.hasLoadedCascades && this.hasCascadeType('notifications')
        },
        showCrmMessagesColumn() {
            if (this.isCustomCascadesMode) return false
            return this.hasLoadedCascades && this.hasCascadeType('crmMessages')
        },
        isTableLoading() {
            if (this.isCustomCascadesMode) {
                return this.isInitialTableLoading || this.isLoadingSlots || this.isLoadingCustomCascades
            }
            return this.isInitialTableLoading || this.isLoadingSlots
        },
        billingState() {
            if (!this.project?.billingEnabled) return 'disabled'
            const billingStore = useBillingStore()
            const tariff = billingStore.getCurrentTariff(this.projectId)
            if (!tariff) return 'no-tariff'
            return 'active'
        },
        isTariffBlocked() {
            if (this.billingState !== 'no-tariff') return false
            return Boolean(useBillingStore().getPreviousTariff(this.projectId))
        },
        noTariffLinkText() {
            return this.isTariffBlocked ? 'Оплатите тариф' : 'Выберите тариф'
        },
        effectiveConnectors() {
            const byId = new Map()
            const withoutId = []
            const addConnector = (connector) => {
                const key = connector?.connectorId || connector?.id || null
                if (!key) {
                    withoutId.push(connector)
                    return
                }
                if (!byId.has(key)) {
                    byId.set(key, connector)
                }
            }
            ;(this.connectors || []).forEach(addConnector)
            ;(this.project?.connectors || []).forEach(addConnector)
            return [...byId.values(), ...withoutId]
        },
        tableItems() {
            const { tariffItems, additionalItems } = this.groupedItems
            return [...tariffItems, ...additionalItems]
        },
        groupedItems() {
            const slots = this.channelSlots
            const pendingAdditionalItems = this.pendingSlots
                .filter(payload => payload?.type !== 'module')
                .map((payload, idx) => ({
                isActive: false,
                _isPendingPayment: true,
                _slotId: `pending-${payload?.id || idx}`,
                _channelDeletedDate: null,
                _paymentMethod: payload?.payment_method || null,
                isAdditional: true,
                }))
            const isRenderableChannelConnector = (connector) => {
                return this.isChannelType(connector) && !!connector?.channelId
            }
            if (!slots || slots.length === 0) {
                return { tariffItems: [], additionalItems: pendingAdditionalItems }
            }

            const projectConnectors = this.effectiveConnectors
            const tariffItems = []
            const additionalItems = []

            slots.forEach(slot => {
                const slotMeta = {
                    _slotId: slot.id,
                    _channelDeletedDate: slot.channelDeletedDate || null,
                    _slotType: slot.type || null,
                }
                if (slot.channelConnectorId) {
                    const connector = projectConnectors.find(
                        c => c.connectorId === slot.channelConnectorId && isRenderableChannelConnector(c)
                    )
                    if (connector) {
                        const item = {
                            ...connector,
                            ...slotMeta,
                        }
                        if (slot.isTariff) {
                            tariffItems.push(item)
                        } else {
                            additionalItems.push(item)
                        }
                    } else {
                        // Если слот ссылается на отсутствующий коннектор,
                        // показываем его как пустой слот.
                        const item = {
                            isActive: false,
                            ...slotMeta,
                            isAdditional: !slot.isTariff,
                        }
                        if (slot.isTariff) {
                            tariffItems.push(item)
                        } else {
                            additionalItems.push(item)
                        }
                    }
                } else {
                    const item = {
                        isActive: false,
                        ...slotMeta,
                        isAdditional: !slot.isTariff,
                    }
                    if (slot.isTariff) {
                        tariffItems.push(item)
                    } else {
                        additionalItems.push(item)
                    }
                }
            })

            const sortFilled = (a, b) => {
                const aFilled = a.isActive !== false ? 0 : 1
                const bFilled = b.isActive !== false ? 0 : 1
                return aFilled - bFilled
            }
            tariffItems.sort(sortFilled)
            additionalItems.sort(sortFilled)

            return { tariffItems, additionalItems: [...additionalItems, ...pendingAdditionalItems] }
        },
        groups() {
            const result = []
            const { tariffItems, additionalItems } = this.groupedItems

            if (tariffItems.length > 0) {
                result.push({ type: 'tariff', label: 'тариф', items: tariffItems })
            } else if (this.billingState === 'no-tariff') {
                result.push({ type: 'no-tariff', label: 'тариф', items: [{ isNoTariff: true }] })
            } else if (this.billingState === 'active') {
                result.push({ type: 'tariff', label: 'тариф', items: [{ isTariffEmpty: true }] })
            }

            if (additionalItems.length > 0) {
                result.push({ type: 'additional', label: 'дополнительный', items: additionalItems })
            }
            return result
        },
    },
    watch: {
        channelPriorities: {
            deep: true,
            handler(newVal) {
                this.localChannelPriorities = this.mergePriorityMaps(this.localChannelPriorities, newVal)
            },
            immediate: true,
        },
        projectId: {
            async handler(newProjectId, oldProjectId) {
                if (!newProjectId || newProjectId === oldProjectId) return
                this.resetCascadesState()
                await this.loadSlots()
                if (this.isCustomCascadesMode) {
                    await this.loadCustomCascades()
                } else {
                    await this.loadChannelPriorities()
                }
            },
        },
        customCascadeDeleteModalOpen(isOpen) {
            if (isOpen) {
                document.addEventListener('keydown', this.onCustomCascadeDeleteEscape)
                return
            }
            document.removeEventListener('keydown', this.onCustomCascadeDeleteEscape)
        },
        useCustomSplitLayout() {
            this.scheduleCustomPanelHeightSync()
            this.$nextTick(() => {
                if (this.useCustomSplitLayout) {
                    this.startCustomPanelResizeTracking()
                } else {
                    this.stopCustomPanelResizeTracking()
                }
            })
        },
        customCascades: {
            deep: true,
            handler() {
                this.scheduleCustomPanelHeightSync()
            },
        },
        tableItems: {
            handler() {
                this.prefetchReworkAuthStatuses()
                // Отдельно от prefetchReworkAuthStatuses: у той функции ранний выход
                // по allChannelsCards, и SMS-статус из-за этого мог не грузиться.
                this.syncSmsGateOnline()
                this.syncMaxBusinessAuthStatuses()
                this.scheduleCustomPanelHeightSync()
            },
            immediate: true,
        },
        isTableLoading(isLoading) {
            if (!isLoading) {
                this.scheduleCustomPanelHeightSync()
            }
        },
        customCascadesExpanded(isExpanded) {
            if (!this.isCustomCascadesMode) return
            this.persistCustomCascadesExpandedToStorage(isExpanded)
            this.$nextTick(() => {
                this.scheduleCustomPanelHeightSync()
                if (isExpanded) {
                    window.setTimeout(() => this.scheduleCustomPanelHeightSync(), 320)
                }
            })
        },
    },
    updated() {
        if (this.useCustomSplitLayout) {
            this.scheduleCustomPanelHeightSync()
        }
        this.syncCustomCascadeNameLabels()
    },
    async mounted() {
        try {
            if (this.projectId) {
                if (this.isCustomCascadesMode) {
                    await Promise.all([
                        this.loadSlots(),
                        this.loadCustomCascades(),
                    ])
                } else {
                    await Promise.all([
                        this.loadSlots(),
                        this.loadChannelPriorities(),
                    ])
                }
            }
        } finally {
            this.isInitialTableLoading = false
        }
        this.$root.$on('reload-channel-priorities', this.handleReloadPriorities)
        this.$root.$on('reload-channel-slots', this.loadSlots)
        this.$root.$on('reload-channel-auth-status', this.handleReloadAuthStatus)
        this.scheduleCustomPanelHeightSync()
        this.startCustomPanelResizeTracking()
    },
    beforeDestroy() {
        this.$root.$off('reload-channel-priorities', this.handleReloadPriorities)
        this.$root.$off('reload-channel-slots', this.loadSlots)
        this.$root.$off('reload-channel-auth-status', this.handleReloadAuthStatus)
        if (this.savePriorityTimer) {
            clearTimeout(this.savePriorityTimer)
        }
        if (this.whatsappBasisAuthInterval) {
            clearInterval(this.whatsappBasisAuthInterval)
            this.whatsappBasisAuthInterval = null
        }
        Object.values(this.channelAuthStatusReloadTimerByConnectorId).forEach(timer => clearTimeout(timer))
        document.removeEventListener('keydown', this.onCustomCascadeDeleteEscape)
        if (this.customPanelHeightSyncTimer) {
            clearTimeout(this.customPanelHeightSyncTimer)
            this.customPanelHeightSyncTimer = null
        }
        this.stopCustomPanelResizeTracking()
    },
    async created() {
        if (this.isCustomCascadesMode) {
            this.customCascadesExpanded = this.readCustomCascadesExpandedFromStorage()
            return
        }
        if (this.project?.isChannelsRework) {
            return
        }
        const connector = (this.connectors || []).find(
            c => c.connectorType === CONNECTOR_TYPES.whatsappBasis
        )
        if (connector) {
            this.whatsappBasisConnectorId = connector.connectorId
            await this.loadWhatsappBasisMetaAndStatus(connector.connectorId)
            this.whatsappBasisAuthInterval = setInterval(() => {
                this.loadWhatsappBasisStatus(connector.connectorId)
            }, 10 * 1000)
        }
    },
    methods: {
        checkAccess,
        checkRole,
        mergePriorityMaps(baseMap = {}, overrideMap = {}) {
            const result = {}
            const keys = new Set([
                ...Object.keys(baseMap || {}),
                ...Object.keys(overrideMap || {}),
            ])
            keys.forEach((key) => {
                const baseValue = baseMap?.[key]
                const overrideValue = overrideMap?.[key]
                result[key] = {
                    ...(baseValue && typeof baseValue === 'object' ? baseValue : {}),
                    ...(overrideValue && typeof overrideValue === 'object' ? overrideValue : {}),
                }
            })
            return result
        },
        hasCascadeType(cascadeType) {
            return Boolean(this.cascades?.[cascadeType])
        },
        resetCascadesState() {
            this.cascades = {}
            this.cascadeIds = {}
            this.hasAnyCascadesOnProject = null
            this.hasLoadedCascades = false
            this.localChannelPriorities = {}
            if (this.isCustomCascadesMode) {
                this.customCascades = []
            }
        },
        shouldUseMaxBusinessAuthStatus(item) {
            return getShouldUseMaxBusinessAuthStatus(item)
        },
        shouldUseSmsGateAuthStatus(item) {
            return getShouldUseSmsGateAuthStatus(item)
        },
        shouldUseLiveAuthStatus(item) {
            return getShouldUseLiveAuthStatus(item)
        },
        getAuthStatusConnectorId(item) {
            return getHelperAuthStatusConnectorId(item)
        },
        isAuthStatusLoading(item) {
            return getIsAuthStatusLoading(item, this.channelAuthStatusLoadingByConnectorId)
        },
        isSmsGateChannel(item) {
            if (!item) return false
            // Поля у элементов списка разнородны: активные каналы несут providerType
            // из Firebase, неактивные — channelType, плюс встречается написание
            // 'sms-gate' (тип канала в БД) против 'smsGate' (тип провайдера в UI).
            // Опираемся ещё и на connectorType — он стабильно 'smsChannel'.
            const provider = item.providerType || item.channelType
            return (
                item.connectorType === CHANNEL_TYPES.smsChannel ||
                provider === PROVIDER_TYPES.smsGate ||
                provider === 'sms-gate'
            )
        },
        isSmsChannelOffline(item) {
            if (!this.isSmsGateChannel(item)) return false
            if (!this.isChannelAuthorized(item)) return false
            const connectorId = this.getAuthStatusConnectorId(item)
            // Пугаем только при ЯВНОМ false: undefined/null — ещё не знаем.
            return this.smsOnlineByConnectorId[connectorId] === false
        },
        syncSmsGateOnline() {
            const items = this.tableItems || []
            items.forEach((item) => {
                if (!this.shouldUseSmsGateAuthStatus(item)) return
                this.fetchReworkAuthStatus(item)
            })
        },
        applySmsGateStatusResponse(connectorId, statusResponse) {
            this.$set(this.smsOnlineByConnectorId, connectorId, getSmsGateOnline(statusResponse))
        },
        async fetchReworkAuthStatus(item) {
            const connectorId = this.getAuthStatusConnectorId(item)
            if (!connectorId) return
            if (this.channelAuthStatusLoadingByConnectorId[connectorId]) return
            if (this.channelAuthStatusByConnectorId[connectorId] !== undefined) return

            this.$set(this.channelAuthStatusLoadingByConnectorId, connectorId, true)
            try {
                const { authorized, error } = await this.fetchLiveAuthAuthorized(connectorId, item)
                if (error) {
                    this.$set(this.channelAuthStatusByConnectorId, connectorId, null)
                    return
                }
                this.$set(this.channelAuthStatusByConnectorId, connectorId, authorized)
            } catch (e) {
                this.$set(this.channelAuthStatusByConnectorId, connectorId, null)
                console.error('Ошибка загрузки authStatusRework:', e)
            } finally {
                this.$set(this.channelAuthStatusLoadingByConnectorId, connectorId, false)
                if (this.channelAuthStatusReloadQueuedByConnectorId[connectorId]) {
                    this.$delete(this.channelAuthStatusReloadQueuedByConnectorId, connectorId)
                    this.reloadReworkAuthStatus(connectorId)
                }
            }
        },
        async reloadReworkAuthStatus(connectorId) {
            if (!connectorId) return
            if (this.channelAuthStatusLoadingByConnectorId[connectorId]) {
                this.$set(this.channelAuthStatusReloadQueuedByConnectorId, connectorId, true)
                return
            }
            this.$delete(this.channelAuthStatusByConnectorId, connectorId)
            this.$set(this.channelAuthStatusLoadingByConnectorId, connectorId, true)
            try {
                const item = this.tableItems.find(i => this.getAuthStatusConnectorId(i) === connectorId)
                    || this.effectiveConnectors.find(c => this.getAuthStatusConnectorId(c) === connectorId)
                const { authorized, error } = await this.fetchLiveAuthAuthorized(connectorId, item)
                if (error) {
                    this.$set(this.channelAuthStatusByConnectorId, connectorId, null)
                    return
                }
                this.$set(this.channelAuthStatusByConnectorId, connectorId, authorized)
            } catch (e) {
                this.$set(this.channelAuthStatusByConnectorId, connectorId, null)
                console.error('Ошибка перезагрузки authStatusRework:', e)
            } finally {
                this.$set(this.channelAuthStatusLoadingByConnectorId, connectorId, false)
                if (this.channelAuthStatusReloadQueuedByConnectorId[connectorId]) {
                    this.$delete(this.channelAuthStatusReloadQueuedByConnectorId, connectorId)
                    this.reloadReworkAuthStatus(connectorId)
                }
            }
        },
        async fetchLiveAuthAuthorized(connectorId, item) {
            if (this.shouldUseSmsGateAuthStatus(item)) {
                const statusResponse = await api.networkFixer.getSmsGateStatus({ connectorId })
                this.applySmsGateStatusResponse(connectorId, statusResponse)
                if (statusResponse?.error) {
                    return { authorized: null, error: true }
                }
                return {
                    authorized: isSmsGateStatusAuthorized(statusResponse),
                    error: false,
                }
            }
            if (this.shouldUseMaxBusinessAuthStatus(item)) {
                const statusResponse = await api.networkFixer.getMaxBusinessStatus({ connectorId })
                if (statusResponse?.error) {
                    return { authorized: null, error: true }
                }
                return {
                    authorized: isMaxBusinessStatusAuthorized(statusResponse),
                    error: false,
                }
            }
            const { data: authData, error: authError } = await api.networkFixer.getAuthStatusRework({ connectorId })
            if (authError) {
                return { authorized: null, error: true }
            }
            return { authorized: authData === 'authorized', error: false }
        },
        handleReloadAuthStatus(payload) {
            const connectorId = typeof payload === 'string' ? payload : payload?.connectorId
            if (!connectorId) return
            const item = this.tableItems.find(i => this.getAuthStatusConnectorId(i) === connectorId)
            if (item && this.shouldUseLiveAuthStatus(item)) {
                if (this.channelAuthStatusReloadTimerByConnectorId[connectorId]) {
                    clearTimeout(this.channelAuthStatusReloadTimerByConnectorId[connectorId])
                }
                const timer = setTimeout(() => {
                    this.$delete(this.channelAuthStatusReloadTimerByConnectorId, connectorId)
                    this.reloadReworkAuthStatus(connectorId)
                }, 400)
                this.$set(this.channelAuthStatusReloadTimerByConnectorId, connectorId, timer)
            }
        },
        prefetchReworkAuthStatuses() {
            if (!this.allChannelsCards) return
            this.tableItems.forEach((item) => {
                if (!this.shouldUseLiveAuthStatus(item)) return
                this.fetchReworkAuthStatus(item)
            })
        },
        syncMaxBusinessAuthStatuses() {
            const items = this.tableItems || []
            items.forEach((item) => {
                if (!this.shouldUseMaxBusinessAuthStatus(item)) return
                this.fetchReworkAuthStatus(item)
            })
        },
        openTariffsPage() {
            this.$router.push(`/project/${this.project.id}/billing/?section=tariffs&highlighted=true`)
        },
        openNoTariffBillingPage() {
            if (this.isTariffBlocked) {
                this.$router.push(`/project/${this.project.id}/billing/?section=subscriptions`)
                return
            }
            this.openTariffsPage()
        },
        getItemKey(item) {
            if (item.isNoTariff) return 'no-tariff-placeholder'
            if (item.isTariffEmpty) return 'tariff-empty-placeholder'
            if (item._slotId) return `slot-${item._slotId}`
            if (item.isActive === false) {
                return `inactive-${item.channelType}-${item.futureIndex || 0}`
            }
            return item.connectorId || item.id || Math.random()
        },
        isMaxBusinessSlot(item) {
            return item?._slotType === 'maxSlot'
        },
        getConnectorPhone(item) {
            if (!item || item.isActive === false || item.isNoTariff) return ''
            const connectorId = item.connectorId
            if (!connectorId) return item.phone || ''
            const liveConnector = this.effectiveConnectors.find(c => c.connectorId === connectorId)
            return liveConnector?.phone || item.phone || ''
        },
        // ─── Channel rendering ───────────────────────────────────────
        getConnectorAvatar(item) {
            if (item.isActive === false) {
                const channelType = PROVIDER_TO_CHANNEL_MAP[item.channelType]
                const avatar = this.connectorsAvatars.find(a => a.type === channelType) || {
                    color: 'white', symbol: '?', name: 'Неизвестно',
                }
                const baseNames = {
                    [PROVIDER_TYPES.whatsappBasis]: 'WhatsApp',
                    [PROVIDER_TYPES.whatsappApi]: 'WhatsApp',
                    [PROVIDER_TYPES.whatsapp]: 'WhatsApp',
                    [PROVIDER_TYPES.whatsappDh]: 'WhatsApp',
                    [PROVIDER_TYPES.telegramApi]: 'Telegram',
                    [PROVIDER_TYPES.telegramDh]: 'Telegram',
                    [PROVIDER_TYPES.maxBasis]: 'MAX',
                    [PROVIDER_TYPES.maxDh]: 'MAX',
                    [PROVIDER_TYPES.maxBusiness]: 'MAX Business',
                    [PROVIDER_TYPES.smsGate]: 'SMS',
                }
                const baseName = baseNames[item.channelType] || item.channelType

                let targetChannelType = null
                if ([PROVIDER_TYPES.whatsappApi, PROVIDER_TYPES.whatsappBasis, PROVIDER_TYPES.whatsapp, PROVIDER_TYPES.whatsappDh].includes(item.channelType)) {
                    targetChannelType = CHANNEL_TYPES.whatsappChannel
                } else if ([PROVIDER_TYPES.telegramApi, PROVIDER_TYPES.telegramDh].includes(item.channelType)) {
                    targetChannelType = CHANNEL_TYPES.telegramChannel
                } else if (item.channelType === PROVIDER_TYPES.maxBasis || item.channelType === PROVIDER_TYPES.maxDh) {
                    targetChannelType = CHANNEL_TYPES.maxChannel
                } else if (item.channelType === PROVIDER_TYPES.maxBusiness) {
                    targetChannelType = CHANNEL_TYPES.maxBusiness
                } else if (item.channelType === PROVIDER_TYPES.smsGate) {
                    targetChannelType = CHANNEL_TYPES.smsChannel
                }

                let channelIndex = 1
                if (targetChannelType) {
                    channelIndex = this.getChannelIndex(targetChannelType, null)
                }

                const displayName = channelIndex === 1 ? baseName : `${baseName} ${channelIndex}`
                return { ...avatar, name: displayName }
            }

            const isWhatsappBasis = item.connectorType === CONNECTOR_TYPES.whatsappBasis
            const isChannel = Object.values(CHANNEL_TYPES).includes(item.connectorType) ||
                             (item.providerType && Object.values(PROVIDER_TYPES).includes(item.providerType)) ||
                             Object.values(PROVIDER_TYPES).includes(item.connectorType)

            const avatarType = isChannel ? item.connectorType : (item.providerType || item.connectorType)
            const baseAvatar = this.connectorsAvatars.find(a => a.type === avatarType) || {
                color: 'white', symbol: '404', name: 'Не найдено',
            }

            if (isChannel) {
                const providerType = item.providerType || item.connectorType
                if (this.project.isChannelsRework && isWhatsappBasis) {
                    const cachedName = this.whatsappBasisNames[item.connectorId]
                    if (cachedName) {
                        let avatar = baseAvatar
                        if (cachedName.includes('MAX')) {
                            const maxAvatar = this.connectorsAvatars.find(a => a.type === CHANNEL_TYPES.maxChannel)
                            if (maxAvatar) avatar = maxAvatar
                        } else if (cachedName.includes('Telegram')) {
                            const tgAvatar = this.connectorsAvatars.find(a => a.type === CHANNEL_TYPES.telegramChannel)
                            if (tgAvatar) avatar = tgAvatar
                        }
                        return { ...avatar, name: cachedName }
                    }
                    return { ...baseAvatar, name: 'WhatsApp *' }
                }
                const channelIndex = this.getChannelIndex(providerType, item)
                const displayName = this.getChannelDisplayName(providerType, channelIndex)
                return { ...baseAvatar, name: displayName }
            }
            return baseAvatar
        },
        getChannelIndex(channelType, connector) {
            let targetChannelType = null
            if ([CHANNEL_TYPES.whatsappChannel, PROVIDER_TYPES.whatsappApi, PROVIDER_TYPES.whatsappBasis, PROVIDER_TYPES.whatsapp, PROVIDER_TYPES.whatsappDh].includes(channelType)) {
                targetChannelType = CHANNEL_TYPES.whatsappChannel
            } else if ([CHANNEL_TYPES.telegramChannel, PROVIDER_TYPES.telegramApi, PROVIDER_TYPES.telegramDh].includes(channelType)) {
                targetChannelType = CHANNEL_TYPES.telegramChannel
            } else if ([CHANNEL_TYPES.maxChannel, PROVIDER_TYPES.maxBasis, PROVIDER_TYPES.maxDh].includes(channelType)) {
                targetChannelType = CHANNEL_TYPES.maxChannel
            } else if ([CHANNEL_TYPES.maxBusiness, PROVIDER_TYPES.maxBusiness].includes(channelType)) {
                targetChannelType = CHANNEL_TYPES.maxBusiness
            } else if ([CHANNEL_TYPES.smsChannel, PROVIDER_TYPES.smsGate].includes(channelType)) {
                targetChannelType = CHANNEL_TYPES.smsChannel
            } else {
                return 1
            }

            const channelsInGroup = this.effectiveConnectors
                .filter(c => !c.isChisai && c.connectorType === targetChannelType)
                .sort((a, b) => {
                    const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                    const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return aTime - bTime
                    if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
                    if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
                    return a.connectorId.localeCompare(b.connectorId)
                })

            if (connector) {
                const currentIndex = channelsInGroup.findIndex(c => c.connectorId === connector.connectorId)
                return currentIndex >= 0 ? currentIndex + 1 : channelsInGroup.length + 1
            }
            return channelsInGroup.length + 1
        },
        getChannelDisplayName(channelType, index) {
            const baseNames = {
                [PROVIDER_TYPES.whatsappBasis]: 'WhatsApp',
                [PROVIDER_TYPES.whatsappApi]: 'WhatsApp',
                [PROVIDER_TYPES.whatsapp]: 'WhatsApp',
                [PROVIDER_TYPES.whatsappDh]: 'WhatsApp',
                [PROVIDER_TYPES.telegramApi]: 'Telegram',
                [PROVIDER_TYPES.telegramDh]: 'Telegram',
                [PROVIDER_TYPES.maxBasis]: 'MAX',
                [PROVIDER_TYPES.maxDh]: 'MAX',
                [PROVIDER_TYPES.maxBusiness]: 'MAX Business',
                [PROVIDER_TYPES.smsGate]: 'SMS',
            }
            const baseName = baseNames[channelType] || channelType
            return index === 1 ? baseName : `${baseName} ${index}`
        },

        // ─── Auth / status ───────────────────────────────────────────
        isChannelAuthorized(item) {
            return getIsChannelAuthorized(item, {
                statusByConnectorId: this.channelAuthStatusByConnectorId,
                loadingByConnectorId: this.channelAuthStatusLoadingByConnectorId,
            })
        },
        isHrTelegram(item) {
            return item.connectorType === CONNECTOR_TYPES.telegramHr ||
                   item.connectorType === CONNECTOR_TYPES.telegramHrBranch
        },
        isChannelType(item) {
            return Object.values(CHANNEL_TYPES).includes(item.connectorType)
        },
        canOpenChannelSettings(item) {
            if (!checkRole(this.projectId, 'project:connector:edit')) return false
            if (this.isHrTelegram(item)) return false
            return item.isActive !== false && !item.isDraft && (item.connectorId || item.id)
        },
        canFinishConnectorCreate(item) {
            if (!checkRole(this.projectId, 'project:connector:edit')) return false
            return item.isDraft && item.isActive !== false && (item.connectorId || item.id)
        },
        canHandleAuthCellClick(item) {
            if (!item || item._isPendingPayment || item.isNoTariff || item.isTariffEmpty) return false
            return (
                this.canOpenChannelSettings(item) ||
                this.canFinishConnectorCreate(item) ||
                item.isActive === false
            )
        },
        handleAuthCellClick(item) {
            if (!this.canHandleAuthCellClick(item)) return
            this.handleRowClick(item)
        },
        handleRowClick(item) {
            if (!item) return
            if (!checkRole(this.projectId, 'project:connector:edit')) return
            if (item.isActive === false) {
                this.connectInactiveChannel(item)
                return
            }
            if (this.isHrTelegram(item)) return
            if (item.connectorId || item.id) {
                this.openConnectorSettingsInSidebar(item)
            }
        },
        openConnectorSettingsInSidebar(connector) {
            const connectorId = connector.connectorId || connector.id
            if (connectorId) {
                this.$emit('openConnectorSettingsInSidebar', connectorId)
            }
        },
        shouldShowQrCodeButton(item) {
            return getShouldShowQrCodeButton(item)
        },
        hasSlotPaymentDeadline(item) {
            return !!item?._channelDeletedDate
        },
        shouldShowFrozenStatus(item) {
            return this.hasSlotPaymentDeadline(item) || this.billingState === 'no-tariff'
        },
        getPendingPaymentSubtext(item) {
            if (item?._paymentMethod === 'yookassa') {
                return 'Ожидаем поступление. Обычно до 5 минут'
            }
            return 'Ожидаем поступление. Обычно 1-3 дня'
        },
        frozenStatusSubtext(item) {
            if (this.billingState !== 'no-tariff') {
                return `Оплатить до ${this.getSlotPaymentDeadlineLabel(item)}`
            }
            const raw = useBillingStore().getBlockedTariffPayUntil(this.projectId)
            if (!raw) return 'Оплатите тариф'
            const dt = new Date(raw)
            if (Number.isNaN(dt.getTime())) return 'Оплатите тариф'
            return `Оплатить до ${dt.toLocaleDateString('ru-RU')}`
        },
        getSlotPaymentDeadlineLabel(item) {
            const raw = item?._channelDeletedDate
            if (!raw) return ''
            const dt = new Date(raw)
            if (Number.isNaN(dt.getTime())) return ''
            dt.setDate(dt.getDate() - 1)
            return dt.toLocaleDateString('ru-RU')
        },

        // ─── WhatsApp Basis meta ─────────────────────────────────────
        async loadWhatsappBasisMetaAndStatus(connectorId) {
            if (!connectorId) return
            try {
                const [app, error] = await api.networkFixer
                    .getApp({ connectorId })
                    .then(res => [res.data?.data, res.error])

                let name = 'WhatsApp *'
                if (!error && app) {
                    this.whatsappBasisChannelType = Object.keys(app?.channel || {})[0] || null
                    if (app?.channel?.['wApi-telegram']?.credentials || app?.channel?.['dh-telegram']?.credentials) {
                        name = 'Telegram *'
                    } else if (app?.channel?.['green-api-max']?.credentials) {
                        name = 'MAX *'
                    }
                }
                this.$set(this.whatsappBasisNames, connectorId, name)
                await this.loadWhatsappBasisStatus(connectorId)
            } catch (e) {
                this.$set(this.whatsappBasisNames, connectorId, 'WhatsApp *')
            }
        },
        async loadWhatsappBasisStatus(connectorId) {
            if (!connectorId) return
            try {
                const { data: authData, error: authError } = await api.networkFixer.getAuthStatus({ connectorId })
                if (!authError) {
                    this.whatsappBasisAuthStatus = authData?.alive ?? null
                }
            } catch (e) {
                // ignore
            }
        },
        async shareWhatsappBasisQrCode(item) {
            if (!item || !item.connectorId) return
            const res = await api.networkFixer.getNoAuthToken({ connectorId: item.connectorId })
            if (!res.error) {
                const token = res.data.token
                const project = this.project
                const isChannelsRework = project?.isChannelsRework ? 'true' : 'false'

                let provider
                if (project?.isChannelsRework) {
                    // Ссылка «Поделиться QR-кодом» ведёт на наш провайдер: у стороннего
                    // канал заберёт проверка в getDhWaQr/getDhMaxQr, как у телеграма.
                    const FORCED_PROVIDER_ROUTE = {
                        [PROVIDER_TYPES.whatsappBasis]: 'basis-api',
                        [PROVIDER_TYPES.whatsappApi]: 'wApi-whatsapp',
                        [PROVIDER_TYPES.telegramApi]: 'wApi-telegram',
                        [PROVIDER_TYPES.maxBasis]: 'green-api-max',
                    }
                    const providerTypeMap = {
                        [PROVIDER_TYPES.whatsappBasis]: 'dh-whatsapp',
                        [PROVIDER_TYPES.whatsappApi]: 'dh-whatsapp',
                    [PROVIDER_TYPES.whatsappDh]: 'dh-whatsapp',
                    [PROVIDER_TYPES.telegramApi]: 'dh-telegram',
                    [PROVIDER_TYPES.telegramDh]: 'dh-telegram',
                    [PROVIDER_TYPES.maxBasis]: 'dh-max',
                    [PROVIDER_TYPES.maxDh]: 'dh-max',
                }
                    // Рычаг поддержки: если провайдера назначили руками, ссылка ведёт к нему
                    const forced = item.forcedProvider
                    provider = (forced && FORCED_PROVIDER_ROUTE[forced])
                        || providerTypeMap[item.providerType]
                        || this.whatsappBasisChannelType
                } else {
                    provider = this.whatsappBasisChannelType
                }

                const qrCodeUrl = `${window.location.origin}/qr/${item.connectorId}?token=${token}&isChannelsRework=${isChannelsRework}&provider=${provider}`
                await navigator.clipboard.writeText(qrCodeUrl)
                window.open(qrCodeUrl, '_blank')
                this.$store.dispatch('callNotify', 'Ссылка скопирована в буфер обмена')
            } else {
                this.$store.dispatch('callNotify', 'Ошибка при генерации ссылки')
            }
        },

        // ─── Inactive channel connection ─────────────────────────────
        onBuyAdditionalChannelClick() {
            const billingStore = useBillingStore()
            if (typeof billingStore.setAddChannelContext === 'function') {
                billingStore.setAddChannelContext({
                    name: 'WA/TG/MAX/SMS',
                    icon: null,
                    kind: 'bundle',
                })
            }
            billingStore.setPaymentModalOpened(true, false)
        },
        onUpgradeTariffClick() {
            const billingStore = useBillingStore()
            billingStore.setAddChannelContext(null)
            billingStore.selectedTariffId = this.smartBotTariffId
            billingStore.selectedSubscriptionPeriod = String(this.maxSubscriptionPeriodMonths)
            billingStore.setPaymentModalOpened(false)
            if (this.project?.id) {
                this.$router.push(
                    `/project/${this.project.id}/billing/?section=tariffs&highlighted=true&default=${this.smartBotTariffId}&period=${this.maxSubscriptionPeriodMonths}`
                )
            }
        },
        getTariffTier() {
            const billingStore = useBillingStore()
            const tariff = billingStore.getCurrentTariff(this.projectId)
            if (!tariff) return 0
            const billingTariff = billingStore.getTariffById(tariff.integrationId)
            const raw = [
                tariff.integrationId,
                tariff.name,
                billingTariff?.id,
                billingTariff?.name,
                ...(billingTariff?.tags || []),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()

            if (raw.includes('tariff3') || /тариф\s*3/.test(raw)) return 3
            if (raw.includes('tariff2') || /тариф\s*2/.test(raw)) return 2
            if (raw.includes('tariff1') || /тариф\s*1/.test(raw)) return 1
            return 1
        },
        connectInactiveChannel(item, initialTab = 'new') {
            const opened = openChannelCreationSidebar(this, item, initialTab, {
                context: this.isCustomCascadesMode ? 'custom-cascades' : null,
            })
            if (!opened) return
            if (initialTab !== 'transfer' && this.isMaxBusinessSlot(item)) {
                const newUrl = new URL(window.location.href)
                newUrl.searchParams.set('group', 'maxBusiness')
                window.history.replaceState({}, '', newUrl.toString())
            }
        },

        // ─── Priorities ──────────────────────────────────────────────
        async loadSlots(maybeSilent) {
            if (!this.projectId) return
            const silent =
                maybeSilent === true ||
                (maybeSilent && typeof maybeSilent === 'object' && maybeSilent.silent === true)
            if (silent && this.isLoadingSlots) return
            if (silent) {
                this.slotsSilentRefreshing = true
            } else {
                this.isLoadingSlots = true
            }
            try {
                const [{ data, error }, { data: pendingData, error: pendingError }] = await Promise.all([
                    getSlots({ projectId: this.projectId }),
                    getPendingSlots({ projectId: this.projectId }),
                ])
                if (!error) {
                    this.channelSlots = Array.isArray(data) ? data : []
                }
                if (!pendingError) {
                    this.pendingSlots = Array.isArray(pendingData) ? pendingData : []
                } else {
                    this.pendingSlots = []
                }
            } catch (e) {
                this.channelSlots = []
                this.pendingSlots = []
            } finally {
                if (silent) {
                    this.slotsSilentRefreshing = false
                } else {
                    this.isLoadingSlots = false
                }
            }
        },
        handleRefreshPendingSlots() {
            this.$root.$emit('reload-channel-slots', { silent: true })
        },
        getChannelPriority(connectorId, type) {
            const localPriority = this.localChannelPriorities[connectorId]?.[type]
            if (localPriority !== undefined && localPriority !== null) return localPriority
            const propPriority = this.channelPriorities[connectorId]?.[type]
            if (propPriority !== undefined && propPriority !== null) return propPriority
            return null
        },
        async getOrCreateCascade(cascadeType) {
            if (Object.prototype.hasOwnProperty.call(this.cascades || {}, cascadeType)) {
                return this.cascades[cascadeType] || null
            }
            const { data: cascades, error } = await getCascadesByProject(this.projectId)
            this.hasLoadedCascades = true
            if (error) return null
            this.hasAnyCascadesOnProject = Boolean((cascades || []).length > 0)
            ;(cascades || []).forEach((c) => {
                if (!c || !c.cascadeType) return
                this.$set(this.cascades, c.cascadeType, c)
                if (c.id) this.$set(this.cascadeIds, c.cascadeType, c.id)
            })
            const cascade = (cascades || []).find((c) => c && c.cascadeType === cascadeType) || null
            if (!cascade) this.$set(this.cascades, cascadeType, null)
            return cascade
        },
        convertCascadesToPriorities(cascadesData) {
            const priorities = {}
            const projectConnectors = this.effectiveConnectors || []
            if (!projectConnectors.length) return priorities
            const supportedCascadeTypes = ['mailings', 'notifications', 'crmMessages']
            supportedCascadeTypes.forEach(type => {
                const cascade = cascadesData[type]
                if (cascade && cascade.channels) {
                    cascade.channels.forEach(channel => {
                        const connector = projectConnectors.find(
                            c => c?.channelId !== null &&
                                c?.channelId !== undefined &&
                                String(c.channelId) === String(channel.channelId)
                        )
                        if (connector) {
                            const connectorId = connector.connectorId || connector.id
                            if (connectorId) {
                                if (!priorities[connectorId]) priorities[connectorId] = {}
                                priorities[connectorId][type] = channel.priority
                            }
                        }
                    })
                }
            })
            return priorities
        },
        async loadChannelPriorities() {
            if (!this.projectId || this.isLoadingPriorities) return
            this.isLoadingPriorities = true
            try {
                const cascadeTypes = ['mailings', 'notifications', 'crmMessages']
                const cascadesData = {}
                for (const type of cascadeTypes) {
                    const cascade = await this.getOrCreateCascade(type)
                    if (cascade) cascadesData[type] = cascade
                }
                const priorities = this.convertCascadesToPriorities(cascadesData)
                if (Object.keys(priorities).length > 0) {
                    this.localChannelPriorities = this.mergePriorityMaps(priorities, this.channelPriorities)
                    this.$emit('priorities-loaded', this.localChannelPriorities)
                    if (this.hasAnyCascadesOnProject !== false) {
                        await this.normalizeMailingsPrioritiesForDh()
                    }
                } else if (Object.keys(this.channelPriorities).length > 0) {
                    this.localChannelPriorities = this.mergePriorityMaps({}, this.channelPriorities)
                    if (this.hasAnyCascadesOnProject !== false) {
                        await this.normalizeMailingsPrioritiesForDh()
                    }
                }
            } catch (err) {
                if (Object.keys(this.channelPriorities).length > 0) {
                    this.localChannelPriorities = this.mergePriorityMaps({}, this.channelPriorities)
                }
            } finally {
                this.isLoadingPriorities = false
            }
        },
        handleReloadPriorities() {
            if (!this.projectId) return
            if (this.isCustomCascadesMode) {
                return this.loadCustomCascades()
            }
            const isChannelCreationMode = !!this.$store.state.channelCreationMode
            if (isChannelCreationMode) this.$store.commit('startChannelCreationLock')
            this.resetCascadesState()
            Promise.resolve()
                .then(() => this.loadChannelPriorities())
                .finally(() => {
                    if (isChannelCreationMode) {
                        this.$store.commit('endChannelCreationLock')
                        this.$root.$emit('channel-create-priorities-sync-done')
                    }
                })
        },
        getCustomCascadeCreatedAtTimestamp(cascade) {
            const ts = cascade?.createdAt ? new Date(cascade.createdAt).getTime() : Number.MAX_SAFE_INTEGER
            return Number.isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts
        },
        sortCustomCascadesByCreatedAt(cascades) {
            return [...(cascades || [])].sort((a, b) => {
                const aTime = this.getCustomCascadeCreatedAtTimestamp(a)
                const bTime = this.getCustomCascadeCreatedAtTimestamp(b)
                if (aTime !== bTime) return aTime - bTime
                return String(a?.id ?? '').localeCompare(String(b?.id ?? ''))
            })
        },
        getCustomCascadeDisplayNumber(cascade) {
            const index = this.customCascades.findIndex(item => item.id === cascade.id)
            return index >= 0 ? index + 2 : cascade.id
        },
        getCustomScrollRowClasses(item) {
            return {
                'ct-row--frozen': !item.isTariffEmpty && this.shouldShowFrozenStatus(item),
                'ct-row--pending': !!item._isPendingPayment,
            }
        },
        onWindowResizeSync() {
            this.scheduleCustomPanelHeightSync()
        },
        startCustomPanelResizeTracking() {
            if (!this.useCustomSplitLayout || typeof window === 'undefined') return
            this.stopCustomPanelResizeTracking()
            const root = this.$el
            if (!root) return
            if (typeof ResizeObserver !== 'undefined') {
                const observer = new ResizeObserver(() => {
                    this.scheduleCustomPanelHeightSync()
                })
                const panels = root.querySelector('.ct-custom-panels')
                const scrollActions = root.querySelector('.ct-buy-strip-part--scroll .ct-buy-strip__actions')
                if (panels) observer.observe(panels)
                if (scrollActions) observer.observe(scrollActions)
                this.customPanelResizeObserver = observer
                return
            }
            window.addEventListener('resize', this.onWindowResizeSync)
        },
        stopCustomPanelResizeTracking() {
            if (this.customPanelResizeObserver) {
                this.customPanelResizeObserver.disconnect()
                this.customPanelResizeObserver = null
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', this.onWindowResizeSync)
            }
        },
        scheduleCustomPanelHeightSync() {
            if (!this.useCustomSplitLayout) return
            if (this.customPanelHeightSyncTimer) {
                clearTimeout(this.customPanelHeightSyncTimer)
            }
            this.customPanelHeightSyncTimer = setTimeout(() => {
                this.customPanelHeightSyncTimer = null
                this.syncCustomPanelRowHeights()
            }, 0)
        },
        onCustomPanelBodyTransitionEnd(event) {
            if (!this.isCustomCascadesMode || event.propertyName !== 'grid-template-rows') return
            this.scheduleCustomPanelHeightSync()
        },
        syncCustomPanelRowHeights() {
            if (!this.useCustomSplitLayout) return
            this.$nextTick(() => {
                const root = this.$el
                if (!root) return

                const fixedHeader = root.querySelector('.ct-custom-panels__fixed .ct-header')
                const scrollHeader = root.querySelector('.ct-custom-panels__scroll-scroller .ct-header')
                if (fixedHeader && scrollHeader) {
                    fixedHeader.style.minHeight = ''
                    scrollHeader.style.minHeight = ''
                    const headerHeight = Math.max(fixedHeader.offsetHeight, scrollHeader.offsetHeight)
                    fixedHeader.style.minHeight = `${headerHeight}px`
                    scrollHeader.style.minHeight = `${headerHeight}px`
                }

                const fixedRows = root.querySelectorAll('.ct-custom-panels__fixed .ct-row')
                const scrollRows = root.querySelectorAll('.ct-custom-panels__scroll-scroller .ct-row')
                fixedRows.forEach((fixedRow, index) => {
                    const scrollRow = scrollRows[index]
                    if (!scrollRow) return
                    fixedRow.style.minHeight = ''
                    scrollRow.style.minHeight = ''
                    const rowHeight = Math.max(fixedRow.offsetHeight, scrollRow.offsetHeight)
                    fixedRow.style.minHeight = `${rowHeight}px`
                    scrollRow.style.minHeight = `${rowHeight}px`
                })

                const fixedBuyStrip = root.querySelector('.ct-buy-strip-part--fixed')
                const scrollBuyStrip = root.querySelector('.ct-buy-strip-part--scroll')
                const panels = root.querySelector('.ct-custom-panels')
                if (panels) {
                    panels.style.setProperty('--ct-buy-strip-total-w', `${panels.offsetWidth}px`)
                    const scrollBuyStripActions = scrollBuyStrip
                        ? scrollBuyStrip.querySelector('.ct-buy-strip__actions')
                        : null
                    const actionsReserveWidth = scrollBuyStripActions
                        ? scrollBuyStripActions.offsetWidth
                        : 0
                    panels.style.setProperty('--ct-buy-strip-actions-w', `${actionsReserveWidth}px`)
                }
                if (fixedBuyStrip && scrollBuyStrip) {
                    fixedBuyStrip.style.minHeight = ''
                    fixedBuyStrip.style.height = ''
                    scrollBuyStrip.style.minHeight = ''
                    scrollBuyStrip.style.height = ''
                    const buyStripHeight = Math.max(fixedBuyStrip.offsetHeight, scrollBuyStrip.offsetHeight)
                    fixedBuyStrip.style.height = `${buyStripHeight}px`
                    scrollBuyStrip.style.height = `${buyStripHeight}px`
                }
            })
        },
        async loadCustomCascades() {
            if (!this.projectId || this.isLoadingCustomCascades) return
            this.isLoadingCustomCascades = true
            try {
                const { data, error } = await getCascadesByProject(this.projectId)
                if (error) return
                this.customCascades = this.sortCustomCascadesByCreatedAt(
                    (data || []).filter(cascade => cascade?.cascadeType === 'custom'),
                )
            } finally {
                this.isLoadingCustomCascades = false
                this.$nextTick(() => this.syncCustomCascadeNameLabels())
            }
        },
        getCustomCascadePriority(item, cascade) {
            if (!item?.channelId || !cascade?.channels) return null
            const cascadeChannel = cascade.channels.find(ch => String(ch.channelId) === String(item.channelId))
            return cascadeChannel?.priority ?? null
        },
        getMaxPriorityForCustomCascade(cascade) {
            return (cascade?.channels || []).reduce((max, ch) => {
                const value = Number(ch?.priority || 0)
                return value > max ? value : max
            }, 0)
        },
        getExcludedPrioritiesForCustomCascade(item, cascade) {
            const currentPriority = this.getCustomCascadePriority(item, cascade)
            return (cascade?.channels || [])
                .map(ch => ch?.priority)
                .filter(priority => priority !== null && priority !== undefined && priority !== currentPriority)
        },
        async addCustomCascade() {
            if (!this.projectId || this.customCascadeBusy || this.customCascades.length >= this.maxCustomCascades) return
            this.customCascadeBusy = true
            try {
                const { data, error } = await createCascade({
                    projectId: this.projectId,
                    name: `Каскад ${this.customCascades.length + 2}`,
                    cascadeType: 'custom',
                    sendType: 'failover',
                    status: 'active',
                })
                if (error || !data) {
                    this.$store.dispatch('callNotify', 'Не удалось создать каскад')
                    return
                }
                this.customCascades = this.sortCustomCascadesByCreatedAt([...this.customCascades, data])
                this.customCascadesExpanded = true
            } finally {
                this.customCascadeBusy = false
            }
        },
        getCustomCascadesExpandedStorageKey() {
            return `${this.projectId}-channels-custom-cascades-expanded`
        },
        readCustomCascadesExpandedFromStorage() {
            if (!this.projectId) return false
            try {
                return window.localStorage.getItem(this.getCustomCascadesExpandedStorageKey()) === 'true'
            } catch (e) {
                return false
            }
        },
        persistCustomCascadesExpandedToStorage(isExpanded) {
            if (!this.projectId) return
            try {
                window.localStorage.setItem(
                    this.getCustomCascadesExpandedStorageKey(),
                    isExpanded ? 'true' : 'false'
                )
            } catch (e) {
                // ignore write errors (private mode, quota, etc.)
            }
        },
        getCustomCascadeDisplayName(cascade) {
            if (!cascade) return ''
            return cascade.name || `Каскад ${cascade.id}`
        },
        isCustomCascadeNameSaving(cascade) {
            return !!cascade?.id && this.customCascadeSavingNameId === cascade.id
        },
        syncCustomCascadeNameLabels() {
            if (!this.isCustomCascadesMode) return
            this.customCascades.forEach(cascade => {
                if (this.customCascadeEditingId === cascade.id) return
                if (this.isCustomCascadeNameSaving(cascade)) return
                const el = document.getElementById(`casc-name-${cascade.id}`)
                if (!el) return
                const name = this.getCustomCascadeDisplayName(cascade)
                if (el.textContent !== name) {
                    el.textContent = name
                }
            })
        },
        startCustomCascadeRename(cascade) {
            if (!cascade?.id || this.customCascadeBusy || this.isCustomCascadeNameSaving(cascade)) return
            if (this.customCascadeEditingId && this.customCascadeEditingId !== cascade.id) {
                const prevEl = document.getElementById(`casc-name-${this.customCascadeEditingId}`)
                prevEl?.blur()
            }
            this.customCascadeRenameOriginalName = this.getCustomCascadeDisplayName(cascade).slice(
                0,
                this.maxCustomCascadeNameLength
            )
            this.customCascadeEditingId = cascade.id
            this.$nextTick(() => {
                const el = document.getElementById(`casc-name-${cascade.id}`)
                if (!el) return
                el.textContent = this.customCascadeRenameOriginalName
                el.focus()
                const range = document.createRange()
                range.selectNodeContents(el)
                const selection = window.getSelection()
                selection.removeAllRanges()
                selection.addRange(range)
            })
        },
        handleCustomCascadeRenameKeydown(cascade, event) {
            if (this.customCascadeEditingId !== cascade.id) return
            if (event.key === 'Enter') {
                event.preventDefault()
                event.target.blur()
                return
            }
            if (event.key === 'Escape') {
                event.target.textContent = this.customCascadeRenameOriginalName
                this.customCascadeEditingId = null
                event.target.blur()
                return
            }
            if (this.isCustomCascadeRenameInputAllowed(event)) return
            event.preventDefault()
        },
        isCustomCascadeRenameInputAllowed(event) {
            if (event.ctrlKey || event.metaKey || event.altKey) return true
            const navigationKeys = [
                'Backspace',
                'Delete',
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Home',
                'End',
                'Tab',
            ]
            if (navigationKeys.includes(event.key)) return true
            const target = event.target
            const text = target?.textContent || ''
            const selection = window.getSelection()
            const selectedLength = selection?.toString().length || 0
            if (selectedLength > 0) return true
            if (text.length < this.maxCustomCascadeNameLength) return true
            return false
        },
        handleCustomCascadeRenameInput(cascade, event) {
            if (this.customCascadeEditingId !== cascade.id) return
            this.enforceCustomCascadeRenameLength(event.target)
        },
        handleCustomCascadeRenamePaste(cascade, event) {
            if (this.customCascadeEditingId !== cascade.id) return
            event.preventDefault()
            const pastedText = (event.clipboardData?.getData('text/plain') || '')
                .replace(/[\r\n]+/g, ' ')
                .trim()
            if (!pastedText) return
            const target = event.target
            const selection = window.getSelection()
            if (!selection?.rangeCount) return
            const range = selection.getRangeAt(0)
            range.deleteContents()
            const textNode = document.createTextNode(pastedText)
            range.insertNode(textNode)
            range.setStartAfter(textNode)
            range.collapse(true)
            selection.removeAllRanges()
            selection.addRange(range)
            this.enforceCustomCascadeRenameLength(target)
        },
        enforceCustomCascadeRenameLength(element) {
            if (!element) return
            const maxLength = this.maxCustomCascadeNameLength
            const text = element.textContent || ''
            if (text.length <= maxLength) return
            element.textContent = text.slice(0, maxLength)
            const range = document.createRange()
            range.selectNodeContents(element)
            range.collapse(false)
            const selection = window.getSelection()
            selection?.removeAllRanges()
            selection?.addRange(range)
        },
        async finishCustomCascadeRename(cascade, event) {
            if (this.customCascadeEditingId !== cascade.id) return
            const trimmed = (event.target.textContent || '')
                .trim()
                .slice(0, this.maxCustomCascadeNameLength)
            const originalName = this.customCascadeRenameOriginalName.trim()
            this.customCascadeEditingId = null
            if (!trimmed) {
                event.target.textContent = originalName
                return
            }
            event.target.textContent = trimmed
            if (trimmed === originalName) return
            await this.saveCustomCascadeName(cascade, trimmed, originalName)
        },
        async saveCustomCascadeName(cascade, nextName, originalName) {
            if (!cascade?.id || this.isCustomCascadeNameSaving(cascade)) return
            this.customCascadeSavingNameId = cascade.id
            try {
                const { error } = await updateCascade({ cascadeId: cascade.id, name: nextName })
                if (error) {
                    this.$store.dispatch('callNotify', 'Не удалось переименовать каскад')
                    const el = document.getElementById(`casc-name-${cascade.id}`)
                    if (el) el.textContent = originalName
                    return
                }
                const refreshedCascade = await getCascadeById(cascade.id)
                if (refreshedCascade?.data) {
                    this.customCascades = this.sortCustomCascadesByCreatedAt(
                        this.customCascades.map(item =>
                            item.id === cascade.id ? refreshedCascade.data : item,
                        ),
                    )
                } else {
                    this.customCascades = this.sortCustomCascadesByCreatedAt(
                        this.customCascades.map(item =>
                            item.id === cascade.id ? { ...item, name: nextName } : item,
                        ),
                    )
                }
            } finally {
                this.customCascadeSavingNameId = null
                this.$nextTick(() => this.syncCustomCascadeNameLabels())
            }
        },
        async renameCustomCascade(cascade) {
            this.startCustomCascadeRename(cascade)
        },
        async removeCustomCascade(cascade) {
            if (!cascade?.id || this.customCascadeBusy || this.customCascades.length <= 1) return
            this.customCascadePendingDelete = cascade
            this.customCascadeDeleteModalOpen = true
        },
        closeCustomCascadeDeleteModal(force = false) {
            if (!force && this.customCascadeBusy) return
            this.customCascadeDeleteModalOpen = false
            this.customCascadePendingDelete = null
        },
        onCustomCascadeDeleteEscape(event) {
            if (event.key === 'Escape' && !this.customCascadeBusy) {
                this.closeCustomCascadeDeleteModal()
            }
        },
        async confirmRemoveCustomCascade() {
            const cascade = this.customCascadePendingDelete
            if (!cascade?.id || this.customCascadeBusy) return
            this.customCascadeBusy = true
            try {
                const { error } = await deleteCascade(cascade.id)
                if (error) {
                    this.$store.dispatch('callNotify', 'Не удалось удалить каскад')
                    return
                }
                this.customCascades = this.sortCustomCascadesByCreatedAt(
                    this.customCascades.filter(item => item.id !== cascade.id),
                )
                this.closeCustomCascadeDeleteModal(true)
            } finally {
                this.customCascadeBusy = false
            }
        },
        async updateCustomCascadePriority(item, cascade, priority) {
            if (!item?.channelId || !cascade?.id || this.customCascadeBusy) {
                return
            }
            const currentPriority = this.getCustomCascadePriority(item, cascade)
            if (currentPriority === priority) return

            this.customCascadeBusy = true
            try {
                const isRemovalFlow = priority === null || priority === undefined
                let cascadeForPriorityOps = cascade
                if (isRemovalFlow) {
                    const freshCascadeResponse = await getCascadeById(cascade.id)
                    const freshCascade = freshCascadeResponse?.data
                    const cascadeChannel = freshCascade?.channels?.find(
                        ch => String(ch.channelId) === String(item.channelId),
                    )
                    if (cascadeChannel?.id) {
                        const deleteResult = await deleteCascadeChannel(cascadeChannel.id)
                        if (deleteResult?.error) {
                            this.$store.dispatch('callNotify', 'Не удалось удалить канал из каскада')
                            return
                        }
                    }
                    const postDeleteCascade = await getCascadeById(cascade.id)
                    if (postDeleteCascade?.data) {
                        cascadeForPriorityOps = postDeleteCascade.data
                        this.customCascades = this.sortCustomCascadesByCreatedAt(
                            this.customCascades.map(entry =>
                                entry.id === cascade.id ? postDeleteCascade.data : entry,
                            ),
                        )
                    }
                }

                const activeChannels = this.tableItems.filter(
                    row => row.isActive !== false && row.channelId && this.isChannelType(row),
                )
                const prioritiesByChannel = {}
                activeChannels.forEach(channelItem => {
                    const rowPriority = this.getCustomCascadePriority(channelItem, cascadeForPriorityOps)
                    if (rowPriority !== null && rowPriority !== undefined) {
                        prioritiesByChannel[channelItem.channelId] = rowPriority
                    }
                })

                if (isRemovalFlow) {
                    delete prioritiesByChannel[item.channelId]
                } else {
                    const newPriority = priority
                    if (currentPriority === null || currentPriority === undefined) {
                        const channelWithSamePriority = activeChannels.find(channelItem => {
                            const channelId = channelItem.channelId
                            return channelId && String(channelId) !== String(item.channelId) &&
                                prioritiesByChannel[channelId] === newPriority
                        })
                        if (channelWithSamePriority) {
                            activeChannels.forEach(channelItem => {
                                const channelId = channelItem.channelId
                                if (
                                    channelId &&
                                    String(channelId) !== String(item.channelId) &&
                                    prioritiesByChannel[channelId] !== undefined &&
                                    prioritiesByChannel[channelId] >= newPriority
                                ) {
                                    prioritiesByChannel[channelId] += 1
                                }
                            })
                        }
                        prioritiesByChannel[item.channelId] = newPriority
                    } else {
                        prioritiesByChannel[item.channelId] = newPriority
                        const isMovingUp = newPriority < currentPriority
                        if (isMovingUp) {
                            activeChannels.forEach(channelItem => {
                                const channelId = channelItem.channelId
                                if (
                                    channelId &&
                                    String(channelId) !== String(item.channelId) &&
                                    prioritiesByChannel[channelId] !== undefined &&
                                    prioritiesByChannel[channelId] >= newPriority &&
                                    prioritiesByChannel[channelId] < currentPriority
                                ) {
                                    prioritiesByChannel[channelId] += 1
                                }
                            })
                        } else {
                            activeChannels.forEach(channelItem => {
                                const channelId = channelItem.channelId
                                if (
                                    channelId &&
                                    String(channelId) !== String(item.channelId) &&
                                    prioritiesByChannel[channelId] !== undefined &&
                                    prioritiesByChannel[channelId] > currentPriority &&
                                    prioritiesByChannel[channelId] <= newPriority
                                ) {
                                    prioritiesByChannel[channelId] -= 1
                                }
                            })
                        }
                    }
                }

                const normalized = Object.entries(prioritiesByChannel)
                    .sort((a, b) => Number(a[1]) - Number(b[1]))
                    .map(([channelId], index) => ({ channelId: Number(channelId), priority: index + 1 }))
                    .filter(channel => Number.isFinite(channel.channelId))

                if (normalized.length > 0) {
                    const { error } = await updateCascadeChannelsPriorities({
                        cascadeId: cascade.id,
                        channels: normalized,
                    })
                    if (error) {
                        this.$store.dispatch('callNotify', 'Не удалось обновить приоритеты каскада')
                        return
                    }
                }

                const refreshed = await getCascadeById(cascade.id)
                if (refreshed?.data) {
                    this.customCascades = this.sortCustomCascadesByCreatedAt(
                        this.customCascades.map(entry =>
                            entry.id === cascade.id ? refreshed.data : entry,
                        ),
                    )
                }
            } finally {
                this.customCascadeBusy = false
            }
        },
        handleMailingsPriorityInput(item, priority) {
            const connectorId = item?.connectorId || item?.id
            if (this.shouldConfirmMaxPriority(item, priority)) {
                this.openMaxRiskModal({
                    mode: 'main',
                    connectorId,
                    type: 'mailings',
                    priority,
                    context: 'Основной каскад · Маркетинговые рассылки',
                })
                return
            }
            this.updateChannelPriority(connectorId, 'mailings', priority)
        },
        handleCustomCascadePriorityInput(item, cascade, priority) {
            if (this.shouldConfirmMaxPriority(item, priority)) {
                const name = this.getCustomCascadeDisplayName(cascade)
                this.openMaxRiskModal({
                    mode: 'custom',
                    item,
                    cascade,
                    priority,
                    context: name ? `Дополнительный каскад · ${name}` : 'Дополнительный каскад',
                })
                return
            }
            this.updateCustomCascadePriority(item, cascade, priority)
        },
        shouldConfirmMaxPriority(item, priority) {
            if (this.maxRiskModalOpen) return false
            if (priority === null || priority === undefined) return false
            return isRegularMaxChannel(item)
        },
        openMaxRiskModal(pending) {
            this.maxRiskPending = pending
            this.maxRiskContext = pending.context || ''
            this.maxRiskModalOpen = true
        },
        acceptMaxRisk() {
            const pending = this.maxRiskPending
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
            if (!pending) return
            if (pending.mode === 'custom') {
                this.updateCustomCascadePriority(pending.item, pending.cascade, pending.priority)
                return
            }
            this.updateChannelPriority(pending.connectorId, pending.type, pending.priority)
        },
        removeMaxFromMailings() {
            const pending = this.maxRiskPending
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
            if (!pending) return
            if (pending.mode === 'custom') {
                this.updateCustomCascadePriority(pending.item, pending.cascade, null)
                return
            }
            this.updateChannelPriority(pending.connectorId, pending.type, null)
        },
        dismissMaxRisk() {
            this.maxRiskPending = null
            this.maxRiskModalOpen = false
        },
        updateChannelPriority(connectorId, type, priority) {
            if (!connectorId) return
            const item = this.tableItems.find(i => (i.connectorId || i.id) === connectorId)
            if (type === 'mailings' && item && this.isMailingsPriorityLocked(item)) return
            if (type === 'notifications' && item && this.isWhatsappDhChannel(item)) return
            if (!this.localChannelPriorities[connectorId]) {
                this.$set(this.localChannelPriorities, connectorId, {})
            }
            const currentPriority = this.getChannelPriority(connectorId, type)
            const newPriority = priority
            if (currentPriority === newPriority) return

            const activeChannels = this.tableItems.filter(
                i => i.isActive !== false && (i.connectorId || i.id) && this.isChannelType(i)
            )
            const prioritiesCopy = {}
            activeChannels.forEach(i => {
                const id = i.connectorId || i.id
                if (id) {
                    const p = this.getChannelPriority(id, type)
                    if (p !== null && p !== undefined) prioritiesCopy[id] = p
                }
            })

            if (newPriority === null || newPriority === undefined) {
                delete prioritiesCopy[connectorId]
                this.normalizePriorities(prioritiesCopy, activeChannels, type, connectorId)
                return
            }
            if (currentPriority === null || currentPriority === undefined) {
                const channelWithSamePriority = activeChannels.find(i => {
                    const id = i.connectorId || i.id
                    return id && id !== connectorId && prioritiesCopy[id] === newPriority
                })
                if (channelWithSamePriority) {
                    activeChannels.forEach(i => {
                        const id = i.connectorId || i.id
                        if (id && id !== connectorId && prioritiesCopy[id] !== undefined && prioritiesCopy[id] >= newPriority) {
                            prioritiesCopy[id] += 1
                        }
                    })
                }
                prioritiesCopy[connectorId] = newPriority
            } else {
                prioritiesCopy[connectorId] = newPriority
                const isMovingUp = newPriority < currentPriority
                if (isMovingUp) {
                    activeChannels.forEach(i => {
                        const id = i.connectorId || i.id
                        if (id && id !== connectorId && prioritiesCopy[id] !== undefined &&
                            prioritiesCopy[id] >= newPriority && prioritiesCopy[id] < currentPriority) {
                            prioritiesCopy[id] += 1
                        }
                    })
                } else {
                    activeChannels.forEach(i => {
                        const id = i.connectorId || i.id
                        if (id && id !== connectorId && prioritiesCopy[id] !== undefined &&
                            prioritiesCopy[id] > currentPriority && prioritiesCopy[id] <= newPriority) {
                            prioritiesCopy[id] -= 1
                        }
                    })
                }
            }
            this.normalizePriorities(prioritiesCopy, activeChannels, type, connectorId)
        },
        normalizePriorities(prioritiesCopy, activeChannels, type, changedConnectorId) {
            const channelsWithPriorities = activeChannels
                .map(i => ({ id: i.connectorId || i.id, priority: prioritiesCopy[i.connectorId || i.id] || null }))
                .filter(i => i.id && i.priority !== null && i.priority !== undefined)
            channelsWithPriorities.sort((a, b) => a.priority - b.priority)
            const normalizedPriorities = {}
            channelsWithPriorities.forEach((i, idx) => { normalizedPriorities[i.id] = idx + 1 })
            this.applyNormalizedPriorities(normalizedPriorities, activeChannels, type, changedConnectorId)
        },
        applyNormalizedPriorities(normalizedPriorities, activeChannels, type, changedConnectorId) {
            Object.keys(normalizedPriorities).forEach(id => {
                if (!this.localChannelPriorities[id]) this.$set(this.localChannelPriorities, id, {})
                this.$set(this.localChannelPriorities[id], type, normalizedPriorities[id])
                this.$emit('update-channel-priority', { connectorId: id, type, priority: normalizedPriorities[id] })
            })
            if (changedConnectorId && !normalizedPriorities[changedConnectorId]) {
                if (this.localChannelPriorities[changedConnectorId]) {
                    this.$set(this.localChannelPriorities[changedConnectorId], type, null)
                }
                this.$emit('update-channel-priority', { connectorId: changedConnectorId, type, priority: null })
            }
            if (this.savePriorityTimer) clearTimeout(this.savePriorityTimer)
            this.savePriorityTimer = setTimeout(async () => {
                await this.saveChannelPrioritiesBatch(normalizedPriorities, activeChannels, type, changedConnectorId)
            }, 500)
        },
        async saveChannelPrioritiesBatch(normalizedPriorities, activeChannels, type, changedConnectorId) {
            if (!this.projectId) return
            this.$store.commit('startChannelCreationLock')
            try {
                const cascade = await this.getOrCreateCascade(type)
                if (!cascade) {
                    this.$store.dispatch('callNotify', 'Каскад не найден. Создайте каскад на сервере и повторите.')
                    return
                }
                const cascadeId = cascade.id
                const toSave = []
                Object.keys(normalizedPriorities).forEach(cid => {
                    toSave.push({ connectorId: cid, priority: normalizedPriorities[cid] })
                })
                if (changedConnectorId && !normalizedPriorities[changedConnectorId]) {
                    toSave.push({ connectorId: changedConnectorId, priority: null })
                }
                const ensureChannelId = async (connectorId) => {
                    let connector = this.$store.getters['connectorById'](connectorId)
                    if (!connector) return null
                    let channelId = connector.channelId
                    if (!channelId) {
                        const { data: createdChannelId, error: createError } = await createChannel({ connectorId })
                        if (createError || !createdChannelId) return null
                        channelId = createdChannelId
                        await new Promise(resolve => setTimeout(resolve, 300))
                        try {
                            const { data: fetchedConnector } = await fetchConnector(connectorId)
                            if (fetchedConnector) {
                                this.$store.commit('updateConnector', fetchedConnector)
                                channelId = fetchedConnector.channelId || channelId
                            }
                        } catch (e) { /* ignore */ }
                    }
                    return channelId
                }
                const updates = []
                const deletions = []
                for (const item of toSave) {
                    const channelId = await ensureChannelId(item.connectorId)
                    if (!channelId) continue
                    const numericChannelId = Number(channelId)
                    if (!Number.isFinite(numericChannelId)) continue
                    if (item.priority === null || item.priority === undefined) {
                        deletions.push({ connectorId: item.connectorId, channelId: numericChannelId })
                    } else {
                        updates.push({ channelId: numericChannelId, priority: item.priority })
                    }
                }
                const isRemovalFlow = changedConnectorId && !normalizedPriorities[changedConnectorId]
                if (isRemovalFlow) {
                    const changedChannelId = await ensureChannelId(changedConnectorId)
                    if (changedChannelId) {
                        const latestCascadeResponse = await getCascadeById(cascadeId)
                        const latestCascade = latestCascadeResponse.data || this.cascades?.[type] || cascade
                        const cascadeChannelToDelete = latestCascade?.channels?.find(
                            ch => String(ch.channelId) === String(changedChannelId)
                        )
                        if (cascadeChannelToDelete) {
                            const { error } = await deleteCascadeChannel(cascadeChannelToDelete.id)
                            if (error) {
                                this.$store.dispatch('callNotify', 'Ошибка сохранения приоритетов каналов')
                                return
                            }
                            const refreshedCascade = await getCascadeById(cascadeId)
                            if (refreshedCascade.data) this.$set(this.cascades, type, refreshedCascade.data)
                        }
                    }
                }
                if (updates.length > 0) {
                    const { error } = await updateCascadeChannelsPriorities({ cascadeId, channels: updates })
                    if (error) {
                        this.$store.dispatch('callNotify', 'Ошибка сохранения приоритетов каналов')
                    } else {
                        const updatedCascade = await getCascadeById(cascadeId)
                        if (updatedCascade.data) this.$set(this.cascades, type, updatedCascade.data)
                    }
                }
                if (deletions.length > 0) {
                    for (const d of deletions) {
                        if (isRemovalFlow && String(d.connectorId) === String(changedConnectorId)) continue
                        const latestCascade = this.cascades?.[type] || cascade
                        const cascadeChannel = latestCascade?.channels?.find(
                            ch => String(ch.channelId) === String(d.channelId)
                        )
                        if (cascadeChannel) await deleteCascadeChannel(cascadeChannel.id)
                    }
                    const updatedCascade = await getCascadeById(cascadeId)
                    if (updatedCascade.data) this.$set(this.cascades, type, updatedCascade.data)
                }
            } catch (e) {
                console.error('saveChannelPrioritiesBatch error:', e)
            } finally {
                this.$store.commit('endChannelCreationLock')
            }
        },
        getMaxPriorityForType(type) {
            const activeChannels = this.tableItems.filter(
                i => i.isActive !== false && (i.connectorId || i.id) && this.isChannelType(i)
            )
            let maxPriority = 0
            activeChannels.forEach(i => {
                const id = i.connectorId || i.id
                if (id) {
                    const p = this.getChannelPriority(id, type)
                    if (p !== null && p !== undefined && p > maxPriority) maxPriority = p
                }
            })
            return maxPriority
        },
        isWhatsappDhChannel(item) {
            if (item.providerType === PROVIDER_TYPES.whatsapp) return true
            if (item.connector && item.connector.providerType === PROVIDER_TYPES.whatsapp) return true
            if (item.connectorType === CHANNEL_TYPES.whatsappChannel) {
                const connector = this.$store.getters['connectorById'](item.connectorId || item.id)
                if (connector && connector.providerType === PROVIDER_TYPES.whatsapp) return true
            }
            return false
        },
        getFirstWhatsappDhChannel() {
            const whatsappDhChannels = this.tableItems.filter(
                i => i.isActive !== false && (i.connectorId || i.id) && this.isWhatsappDhChannel(i)
            )
            if (whatsappDhChannels.length === 0) return null
            whatsappDhChannels.sort((a, b) => {
                const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                if (aTime !== bTime) return aTime - bTime
                return String(a.connectorId || a.id).localeCompare(String(b.connectorId || b.id))
            })
            return whatsappDhChannels[0]
        },
        getDhLockedMailingsPriority(activeChannelsOverride) {
            const activeChannels = activeChannelsOverride || this.tableItems.filter(
                i => i.isActive !== false && (i.connectorId || i.id) && this.isChannelType(i)
            )
            const nonDh = activeChannels.filter(i => !this.isWhatsappDhChannel(i))
            const hasNonDh = nonDh.length > 0
            let maxNonDh = 0
            nonDh.forEach(i => {
                const id = i.connectorId || i.id
                if (!id) return
                const p = this.getChannelPriority(id, 'mailings')
                if (p !== null && p !== undefined && p > maxNonDh) maxNonDh = p
            })
            if (!hasNonDh) return 1
            return Math.max(2, maxNonDh + 1)
        },
        isMailingsPriorityLocked(item) {
            if (!this.isWhatsappDhChannel(item)) return false
            return !!this.getFirstWhatsappDhChannel()
        },
        getMailingsPriorityForWhatsappDh(item) {
            if (this.isWhatsappDhChannel(item)) {
                const firstChannel = this.getFirstWhatsappDhChannel()
                if (!firstChannel) return null
                const currentId = item.connectorId || item.id
                const firstId = firstChannel.connectorId || firstChannel.id
                if (currentId === firstId) return this.getDhLockedMailingsPriority()
                return null
            }
            return this.getChannelPriority(item.connectorId || item.id, 'mailings')
        },
        getMailingsPriorityTooltip(item) {
            if (!this.isWhatsappDhChannel(item)) return ''
            const firstChannel = this.getFirstWhatsappDhChannel()
            if (!firstChannel) return ''
            const currentId = item.connectorId || item.id
            const firstId = firstChannel.connectorId || firstChannel.id
            if (currentId === firstId) return 'Это бесплатный канал, приоритет изменить нельзя.'
            return 'У вас уже установлен бесплатный канал в качестве последнего приоритета, данный канал используется только для маркетинговых рассылок.'
        },
        getCustomCascadePriorityTooltip(item) {
            if (!this.isWhatsappDhChannel(item)) return ''
            return 'Это бесплатный канал, приоритет изменить нельзя.'
        },
        getNotificationsPriorityForWhatsappDh(item) {
            if (this.isWhatsappDhChannel(item)) return null
            return this.getChannelPriority(item.connectorId || item.id, 'notifications')
        },
        getNotificationsPriorityTooltip(item) {
            if (!this.isWhatsappDhChannel(item)) return ''
            return 'Отправка сервисных уведомлений недоступна через бесплатный канал.'
        },
        getCrmMessagesPriorityForWhatsappDh(item) {
            if (this.isWhatsappDhChannel(item)) return null
            return this.getChannelPriority(item.connectorId || item.id, 'crmMessages')
        },
        getCrmMessagesPriorityTooltip(item) {
            if (!this.isWhatsappDhChannel(item)) return ''
            return 'Отправка кодов подтверждения из YCLIENTS недоступна через бесплатный канал.'
        },
        getExcludedPriorities(currentConnectorId, type) {
            const activeChannels = this.tableItems.filter(
                i => i.isActive !== false && (i.connectorId || i.id) && this.isChannelType(i)
            )
            const excludedPriorities = []
            activeChannels.forEach(i => {
                const id = i.connectorId || i.id
                if (id !== currentConnectorId) {
                    const p = this.getChannelPriority(id, type)
                    if (p !== null && p !== undefined && p > 0) excludedPriorities.push(p)
                }
            })
            if (type === 'mailings') {
                const firstDh = this.getFirstWhatsappDhChannel()
                if (firstDh) {
                    const locked = this.getDhLockedMailingsPriority(
                        this.tableItems.filter(i => i.isActive !== false && (i.connectorId || i.id) && this.isChannelType(i))
                    )
                    if (locked > 0) excludedPriorities.push(locked)
                }
            }
            return excludedPriorities
        },
        async normalizeMailingsPrioritiesForDh() {
            try {
                const activeChannels = this.tableItems.filter(
                    i => i.isActive !== false && (i.connectorId || i.id) && this.isChannelType(i)
                )
                if (activeChannels.length === 0) return
                const dhChannels = activeChannels.filter(i => this.isWhatsappDhChannel(i))
                if (dhChannels.length === 0) return
                const firstDh = this.getFirstWhatsappDhChannel()
                if (!firstDh) return
                const firstDhId = firstDh.connectorId || firstDh.id

                const nonDh = activeChannels.filter(i => !this.isWhatsappDhChannel(i))
                const nonDhWithPriority = nonDh
                    .map(i => ({ id: i.connectorId || i.id, priority: this.getChannelPriority(i.connectorId || i.id, 'mailings') }))
                    .filter(p => p.id && p.priority !== null && p.priority !== undefined)
                    .sort((a, b) => a.priority - b.priority)

                const desired = {}
                nonDhWithPriority.forEach((p, idx) => { desired[p.id] = idx + 1 })

                const maxNonDhAfterNormalize = nonDhWithPriority.length
                const hasNonDh = nonDh.length > 0
                const lockedDhPriority = hasNonDh ? Math.max(2, maxNonDhAfterNormalize + 1) : 1
                desired[firstDhId] = lockedDhPriority

                const otherDhIds = dhChannels
                    .map(i => i.connectorId || i.id)
                    .filter(id => id && id !== firstDhId)

                const changes = []
                Object.keys(desired).forEach(id => {
                    const next = desired[id]
                    const cur = this.getChannelPriority(id, 'mailings')
                    if (cur !== next) {
                        if (!this.localChannelPriorities[id]) this.$set(this.localChannelPriorities, id, {})
                        this.$set(this.localChannelPriorities[id], 'mailings', next)
                        changes.push({ id, priority: next })
                    }
                })
                otherDhIds.forEach(id => {
                    const cur = this.getChannelPriority(id, 'mailings')
                    if (cur !== null && cur !== undefined) {
                        if (!this.localChannelPriorities[id]) this.$set(this.localChannelPriorities, id, {})
                        this.$set(this.localChannelPriorities[id], 'mailings', null)
                        changes.push({ id, priority: null })
                    }
                })
                if (changes.length === 0) return
                const normalized = {}
                changes.forEach(({ id, priority }) => {
                    if (priority !== null && priority !== undefined) normalized[id] = priority
                })
                await this.saveChannelPrioritiesBatch(normalized, activeChannels, 'mailings', null)
            } catch (e) {
                console.error('normalizeMailingsPrioritiesForDh error:', e)
            }
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'
.channels-table
    display: flex
    flex-direction: column
    width: 100%

.channels-table:not(.channels-table--custom)
    .ct-table-content,
    .ct-header,
    .ct-row,
    .ct-group__rows
        width: 100%
        box-sizing: border-box

    .ct-header__cell--priority,
    .ct-cell--priority
        flex: 1 1 0
        width: auto
        min-width: 0
        max-width: none

    .ct-header__cell--priority
        overflow: hidden
        box-sizing: border-box

.ct-main-cascade-label
    display: flex
    align-items: center
    gap: 8px
    flex-wrap: wrap
    margin-bottom: 10px

.ct-main-cascade-label__badge
    font-size: 14px
    font-weight: 900
    color: #2A3B51
    letter-spacing: -0.015em

.ct-main-cascade-label__title
    font-size: 14px
    font-weight: 800
    color: #2A3B51

.ct-main-cascade-label__desc
    font-size: 12px
    color: #7F8997

.ct-body
    position: relative

.ct-loading-overlay
    position: absolute
    inset: 0
    z-index: 5
    background: rgba(255, 255, 255, 0.74)
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 8px

.ct-loading-overlay__label
    font-size: 13px
    color: #2A3B51

// ─── Header ─────────────────────────────────────────────────────
.ct-header
    display: flex
    align-items: stretch
    padding-left: 0

.ct-header__rail
    width: 16px
    min-width: 16px
    flex-shrink: 0
    border-top: 1px solid $border-new
    border-left: 1px solid $border-new
    border-top-left-radius: 12px
    border-bottom: 1px solid #EAEBEE
    border-right: 0
    background: #f4f5f8
    box-sizing: border-box

.ct-header__cell
    display: flex
    align-items: center
    gap: 2px
    height: 48px
    padding: 16px
    border-bottom: 1px solid #EAEBEE
    border-top: 1px solid $border-new
    background: #f4f5f8
    &:last-child
        border-right: 1px solid $border-new
        border-top-right-radius: 12px
    &:nth-child(2)
        border-left: 0
        border-top-left-radius: 0
    span
        font-size: 11px
        font-weight: 400
        color: #2A3B51
        line-height: normal

.ct-header__cell--channels
    flex: 0 0 260px
    width: 260px
    min-width: 260px
    max-width: 260px

.ct-header__cell--priority
    width: 180px
    flex-shrink: 0
    justify-content: center
    text-align: center
    overflow: hidden
    min-width: 0
    padding-left: clamp(4px, 1.2vw, 16px)
    padding-right: clamp(4px, 1.2vw, 16px)
    gap: 1px

    > span
        display: flex
        flex-direction: column
        align-items: center
        min-width: 0
        max-width: 100%
        overflow: hidden
        font-size: clamp(7px, 1.1vw + 4px, 11px)
        line-height: 1.1

    strong
        font-weight: 700
        font-size: inherit
        line-height: 1.1
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis
        max-width: 100%

    .ct-header__cell-sub
        font-size: clamp(6px, 0.8vw + 4px, 9px)
        font-weight: 400
        line-height: 1.1
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis
        max-width: 100%

    .ct-hint-icon
        flex-shrink: 0

.ct-hint-icon
    color: rgba(0,0,0,0.38)
    opacity: 0.38
    cursor: pointer
    flex-shrink: 0
    &:hover
        color: black
        opacity: 1

// ─── Group ──────────────────────────────────────────────────────
.ct-group
    display: flex
    align-items: stretch

.ct-group__rail
    width: 16px
    min-width: 16px
    flex-shrink: 0
    align-self: stretch
    position: relative
    box-sizing: border-box

    &--tariff
        background: #E5E7F9
        border-right: 2px solid rgba(82, 90, 213, .3)

    &--no-tariff
        background: #E8EAEE
        border-right: 2px solid rgba(170, 178, 196, .4)

    &--additional
        background: #EBECF0
        border-right: 2px solid rgba(170, 178, 196, .4)

.ct-group__rail-inner
    position: absolute
    top: 0
    left: 0
    right: 0
    bottom: 0
    display: flex
    flex-direction: column
    align-items: center
    justify-content: flex-start
    padding-top: 8px

.ct-group__rail-badge
    writing-mode: vertical-rl
    transform: rotate(180deg)
    font-size: 7px
    font-weight: 800
    letter-spacing: .055em
    text-transform: uppercase
    line-height: 1
    white-space: nowrap
    display: inline-block

    &--tariff
        color: $main-new

    &--no-tariff,
    &--additional
        color: #7F8997

.ct-group__rows
    flex: 1
    min-width: 0
    display: flex
    flex-direction: column

// ─── Buy additional channel strip ────────────────────────────────
.ct-buy-strip
    display: flex
    align-items: stretch
    width: 100%
    background: linear-gradient(90deg, #FBFEEF, #FFFFFF)
    // border-top: 1px solid #EEF0F4
    // border-left: 1px solid $border-new
    border-right: 1px solid $border-new
    border-bottom: 1px solid $border-new
    border-radius: 0 0 12px 12px
    box-sizing: border-box

.ct-buy-strip__rail
    width: var(--ct-custom-rail-w, 16px)
    min-width: var(--ct-custom-rail-w, 16px)
    flex-shrink: 0
    background: #EBECF0
    border-right: 2px solid rgba(170, 178, 196, .4)
    border-bottom-left-radius: 12px

.ct-buy-strip__content
    flex: 1
    display: flex
    align-items: center
    justify-content: space-between
    gap: 16px
    min-width: 0
    padding: 8px 16px 8px 20px

.ct-buy-strip__left
    display: flex
    align-items: center
    gap: 12px
    min-width: 0
    flex: 1 1 auto

.ct-buy-strip__icon
    width: 36px
    height: 36px
    border-radius: 10px
    background: #EEF0FF
    color: $main-new
    display: flex
    align-items: center
    justify-content: center
    font-family: $font-family-base
    font-size: 22px
    font-weight: 800
    line-height: 1
    flex-shrink: 0

.ct-buy-strip__copy
    min-width: 0
    flex: 1 1 auto
    overflow: hidden

.ct-buy-strip__title
    font-size: 13px
    font-weight: 900
    color: $dark-new
    line-height: 1.1
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

.ct-buy-strip__sub
    margin-top: 2px
    font-size: 11px
    color: #7F8997
    line-height: 1.2

.ct-buy-strip__actions
    display: flex
    align-items: center
    gap: 10px
    flex-shrink: 0

.ct-buy-strip__btn
    display: inline-flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    height: 34px
    padding: 0 18px
    border: none
    border-radius: 10px
    background: $dark-new
    color: #fff
    font-family: $font-family-base
    font-size: 11px
    font-weight: 900
    text-transform: uppercase
    letter-spacing: .04em
    cursor: pointer
    box-shadow: 0 4px 14px rgba(26, 26, 46, .14)
    transition: transform .12s ease, filter .12s ease
    white-space: nowrap

    &:hover
        transform: translateY(-1px)
        filter: brightness(1.05)

    &:focus-visible
        outline: 2px solid rgba($main-new, .35)
        outline-offset: 1px

// ─── Row ────────────────────────────────────────────────────────
.ct-row
    display: flex
    align-items: stretch
    width: 100%
    position: relative

.ct-row--pending
    min-height: 53px

    .ct-cell
        align-items: flex-start
        padding-top: 9px
        padding-bottom: 9px

.ct-row--max-slot
    .ct-cell--channel
        align-items: center
        background-image: linear-gradient(90deg, rgba(41, 164, 252, 0.14) 0%, rgba(60, 81, 235, 0.08) 42%, rgba(138, 63, 217, 0.04) 68%, transparent 100%)

.ct-row--max-slot.ct-row--frozen
    .ct-cell--channel
        background-image: linear-gradient(90deg, rgba(41, 164, 252, 0.1) 0%, rgba(60, 81, 235, 0.06) 42%, rgba(138, 63, 217, 0.03) 68%, transparent 100%)

.ct-max-slot-crown-anchor
    position: absolute
    top: 3px
    left: 2px
    width: 0
    height: 0
    z-index: 4
    overflow: visible
    pointer-events: none

    ::v-deep > span
        position: static
        display: block
        width: 0
        height: 0
        overflow: visible

.ct-max-slot-crown
    position: absolute
    top: -1px
    left: -1px
    z-index: 4
    width: 20px
    height: 20px
    border-radius: 999px
    display: flex
    align-items: center
    justify-content: center
    background: linear-gradient(135deg, #29a4fc 0%, #3c51eb 55%, #8a3fd9 100%)
    border: 1px solid rgba(60, 81, 235, 0.35)
    box-shadow: 0 2px 6px rgba(60, 81, 235, 0.24)
    pointer-events: auto

.ct-row--frozen .ct-cell
    background-color: #FFF5EA

.ct-row--clickable
    cursor: pointer
    &:hover .ct-cell
        background-color: rgba(0,0,0,0.04)

// ─── Cells ──────────────────────────────────────────────────────
.ct-cell
    display: flex
    align-items: center
    min-height: 53px
    height: auto
    padding: 8px 16px
    border-bottom: 1px solid #EAEBEE
    background-color: #fff
    box-sizing: border-box
    &:last-child
        border-right: 1px solid $border-new
    // &:nth-child(1)
    //     border-left: 1px solid $border-new

.ct-cell--channel
    width: 260px
    flex: 0 0 260px
    flex-shrink: 0
    gap: 8px
    align-items: flex-start
    padding-left: 20px

.ct-cell--auth
    flex: 1 1 220px
    min-width: 200px
    max-width: 240px

.ct-cell--priority
    width: 180px
    flex-shrink: 0
    justify-content: center

// ─── Channel info ───────────────────────────────────────────────
.ct-channel-info
    display: flex
    flex-direction: column
    flex: 1
    min-width: 0

.ct-channel-name-row
    display: flex
    align-items: center
    gap: 2px
    min-width: 0

.ct-channel-name
    flex: 1
    min-width: 0
    font-weight: 600
    font-size: 16px
    line-height: 1.3
    color: #2A3B51
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

.ct-channel-phone
    font-weight: 400
    font-size: 12px
    line-height: 1.3
    color: rgba(42,59,81,0.87)

// ─── Inactive channel slot ──────────────────────────────────────
.ct-inactive-icon
    width: 36px
    height: 36px
    border: 1px dashed $main-new
    border-radius: 10px
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    background: linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), $main-new

    span
        font-size: 26px
        font-weight: 400
        color: $main-new
        line-height: 1

.ct-inactive-icon--pending
    border: 1px dashed #AAB1B9
    background: linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), #AAB1B9

    span
        color: #AAB1B9

.ct-inactive-text
    font-size: 12px
    line-height: 16px
    color: rgba(42,59,81,0.87)
    cursor: pointer

.ct-link
    color: $main-new
    text-decoration: underline
    text-decoration-style: dotted
    cursor: pointer

.ct-inactive-text--pending
    color: #AAB1B9
    text-decoration: underline
    text-decoration-style: dotted
    cursor: default

.ct-inactive-text-pending-activator
    display: inline-block
    cursor: default

// ─── Auth status ────────────────────────────────────────────────
.ct-auth-col
    display: flex
    flex-direction: column
    gap: 2px
    justify-content: center
    min-width: 0
    width: 100%
    max-width: 100%

.ct-auth-status
    display: flex
    align-items: flex-start
    gap: 3px
    padding: 2px 6px
    min-width: 0
    max-width: 100%

    .v-icon
        flex-shrink: 0
        margin-top: 1px

@keyframes ct-auth-pending-pulse
    0%, 100%
        opacity: 1
    50%
        opacity: 0.45

.ct-auth-status--pending
    animation: ct-auth-pending-pulse 1.35s ease-in-out infinite

.ct-auth-status-pending-row
    display: flex
    align-items: center
    gap: 2px
    flex-wrap: nowrap

.ct-auth-refresh-btn
    display: inline-flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    margin: 0
    padding: 4px
    border: 0
    border-radius: 6px
    background: transparent
    color: #7F8997
    cursor: pointer
    line-height: 0
    outline: none

.ct-auth-refresh-btn:hover:not(:disabled)
    background: rgba(55, 142, 255, 0.08)
    color: $main-new

.ct-auth-refresh-btn:focus-visible
    box-shadow: 0 0 0 2px rgba(55, 142, 255, 0.35)

.ct-auth-refresh-btn:disabled
    cursor: wait
    opacity: 0.55

@keyframes ct-auth-refresh-spin
    to
        transform: rotate(360deg)

.ct-auth-refresh-icon
    display: block
    width: 18px
    height: 18px
    flex-shrink: 0
    color: inherit

.ct-auth-refresh-icon--spinning
    animation: ct-auth-refresh-spin 0.8s linear infinite
    transform-origin: 50% 50%

.ct-auth-status--error
    border-radius: 2px

.ct-auth-status--available
    cursor: pointer
    gap: 3px

.ct-auth-status--frozen
    gap: 3px
    min-height: 18px
    width: fit-content
    padding-right: 6px

.ct-auth-status--deadline
    gap: 4px

.ct-auth-subtext
    font-size: 11px
    font-weight: 400
    line-height: 1.45
    color: #AAB1B9
    padding: 0 0 0 25px
    max-width: 100%
    min-width: 0
    white-space: normal
    word-break: break-word
    overflow-wrap: anywhere

.ct-auth-subtext--deadline
    padding: 0 0 0 18px
    color: #AAB1B9

.ct-auth-subtext--frozen
    color: #AAB1B9

.ct-auth-subtext--offline
    color: #C7770B

.ct-auth-icon
    width: 14px
    height: 14px
    flex-shrink: 0

.ct-auth-icon--ok
    width: 18px
    height: 18px

.ct-auth-label
    font-size: 12px
    font-weight: 400
    line-height: 1.3
    min-width: 0
    white-space: normal
    word-break: break-word
    overflow-wrap: anywhere

.ct-auth-label--ok
    color: #38CD58

.ct-auth-label--error
    color: #EE3A3A

.ct-auth-label--available
    color: $main-new

.ct-auth-label--deadline
    color: #2A3B51

.ct-auth-label--frozen
    color: #FD9C2B

.ct-auth-label--pending
    color: #7F8997

.ct-auth-subtext--pending
    color: #7F8997
    padding: 0 0 0 23px

.ct-qr-btn
    flex-shrink: 0
    margin: -4px -6px -4px 0

// ─── Disabled priority (inactive rows) ──────────────────────────
.ct-priority-disabled
    display: flex
    align-items: center
    justify-content: center
    gap: 12px
    opacity: 0.8
    pointer-events: none

.ct-priority-disabled__label
    font-size: 14px
    font-weight: 400
    color: #666
    white-space: nowrap

    @media (max-width: 890px)
        display: none

.ct-priority-disabled__box
    width: 36px
    height: 36px
    border: 1px solid #E0E0E0
    border-radius: 4px
    display: flex
    align-items: center
    justify-content: center
    background-color: #fff
    font-size: 18px
    font-weight: 400
    color: #666

// ─── Avatar overrides ───────────────────────────────────────────
::v-deep .ct-avatar
    border-radius: 10px !important

.ct-img--inactive
    filter: saturate(0%)

::v-deep .ct-avatar--inactive
    background-color: #9E9E9E !important

// ─── No-tariff slot ──────────────────────────────────────────────
.ct-cell--no-tariff
    gap: 10px
    opacity: 0.75

    .ct-link
        white-space: nowrap

    .ct-no-tariff-subline
        white-space: nowrap

.ct-cell--tariff-empty
    gap: 10px
    opacity: 0.75
    align-items: center
    justify-content: center
    text-align: center
    cursor: default
    .ct-inactive-text
        cursor: default

.channels-table--custom
    margin-top: 18px
    --ct-custom-rail-w: 16px
    --ct-custom-channel-w: 260px
    --ct-custom-status-w: 220px
    --ct-custom-cascade-w: 180px
    --ct-custom-fixed-w: calc(var(--ct-custom-rail-w) + var(--ct-custom-channel-w) + var(--ct-custom-status-w))

    .ct-custom-panel
        width: 100%
        box-sizing: border-box

.ct-custom-panels
    display: contents

.ct-custom-panels__fixed
    display: contents

.ct-table-content--custom-split
    overflow: hidden
    border-radius: 0 0 12px 12px

    .ct-custom-panels
        display: flex
        align-items: flex-start
        width: 100%
        min-width: 0

    .ct-custom-panels__fixed
        flex: 0 0 var(--ct-custom-fixed-w)
        width: var(--ct-custom-fixed-w)
        max-width: var(--ct-custom-fixed-w)
        display: flex
        flex-direction: column
        position: relative
        z-index: 2
        min-width: 0

        .ct-body
            flex: 0 0 auto
            position: relative

            &::after
                content: ''
                position: absolute
                top: 0
                right: 0
                bottom: 0
                width: 8px
                background: linear-gradient(to right, rgba(26, 26, 46, .07), transparent)
                pointer-events: none
                z-index: 3

    .ct-custom-panels__scroll-scroller
        flex: 1
        min-width: 0
        min-height: 0
        display: flex
        flex-direction: column
        overflow-x: auto
        overflow-y: hidden
        -webkit-overflow-scrolling: touch
        box-sizing: border-box
        border-right: 1px solid $border-new

    .ct-custom-panels__scroll-wide
        flex: 0 0 auto
        width: max-content
        min-width: 100%

    .ct-buy-strip-part--fixed
        background-image: linear-gradient(90deg, #FBFEEF 0%, #FFFFFF 100%)
        background-size: var(--ct-buy-strip-total-w, 100%) 100%
        background-repeat: no-repeat

    .ct-buy-strip-part--fixed
        display: flex
        align-items: stretch
        flex: 0 0 auto
        width: var(--ct-buy-strip-total-w, 100%)
        max-width: none
        position: relative
        z-index: 6
        background-position: 0 0
        // border-top: 1px solid #EEF0F4
        // border-left: 1px solid $border-new
        border-right: 1px solid $border-new
        border-bottom: 1px solid $border-new
        border-radius: 0 0 12px 12px

        .ct-buy-strip__rail
            border-bottom-left-radius: 12px

        .ct-buy-strip__content--fixed
            flex: 1
            min-width: 0
            justify-content: flex-start
            padding-right: calc(var(--ct-buy-strip-actions-w, 0px) + 31px)

    .ct-buy-strip-part--scroll
        position: sticky
        left: 0
        z-index: 7
        flex: 0 0 auto
        width: 100%
        max-width: 100%
        box-sizing: border-box
        background: transparent
        border-bottom: 1px solid $border-new
        border-radius: 0 0 12px 0

        .ct-buy-strip__content--scroll
            height: 100%
            justify-content: flex-end
            border-radius: 0 0 12px 0
            position: relative
            z-index: 2

    .ct-header--custom-scroll
        .ct-header__cell--custom-cascade:last-child
            border-right: 0

    .ct-row--scroll .ct-cell--custom-cascade:last-child
        border-right: 0

    .ct-body--custom-scroll
        width: max-content
        min-width: 100%

    .ct-header--custom
        .ct-header__cell
            box-sizing: border-box

    .ct-header--custom-scroll
        padding-left: 0
        width: max-content
        min-width: 100%
        display: flex

        .ct-header__cell
            border-top-left-radius: 0
            border-top-right-radius: 0
            border-left: 0

            &:nth-child(2)
                border-top-left-radius: 0
                border-top-right-radius: 0
                border-left: 0

            &:last-child
                border-top-right-radius: 0

        .ct-header__cell--custom-cascade
            border-top-right-radius: 0

            &:last-child
                border-top-right-radius: 0

        .ct-header__cell--custom-cascade:first-child
            border-left: 0

    .ct-group--scroll
        width: max-content
        min-width: 100%

        .ct-group__rows
            border-left: 0
            width: max-content
            min-width: 100%

    .ct-group__rows--tariff,
    .ct-group__rows--no-tariff,
    .ct-group__rows--additional
        flex: 0 0 calc(var(--ct-custom-channel-w) + var(--ct-custom-status-w))
        width: calc(var(--ct-custom-channel-w) + var(--ct-custom-status-w))
        min-width: calc(var(--ct-custom-channel-w) + var(--ct-custom-status-w))

    .ct-row--custom
        width: calc(var(--ct-custom-channel-w) + var(--ct-custom-status-w))

    .ct-row--scroll
        width: max-content
        min-width: 100%
        display: flex

    .ct-header__cell--custom-cascade,
    .ct-row--scroll .ct-cell--custom-cascade
        flex: 1 1 0
        width: auto
        min-width: var(--ct-custom-cascade-w)
        max-width: none
        box-sizing: border-box

    .ct-cell--auth
        flex: 0 0 var(--ct-custom-status-w)
        width: var(--ct-custom-status-w)
        min-width: var(--ct-custom-status-w)
        max-width: var(--ct-custom-status-w)
        overflow: hidden
        border-right: 0

    .ct-header__cell--channels
        flex: 0 0 var(--ct-custom-channel-w)
        width: var(--ct-custom-channel-w)
        min-width: var(--ct-custom-channel-w)
        max-width: var(--ct-custom-channel-w)

    .ct-cell--channel
        flex: 0 0 var(--ct-custom-channel-w)
        width: var(--ct-custom-channel-w)
        max-width: var(--ct-custom-channel-w)

    .ct-header__cell--status
        flex: 0 0 var(--ct-custom-status-w)
        width: var(--ct-custom-status-w)
        min-width: var(--ct-custom-status-w)
        max-width: var(--ct-custom-status-w)
        border-right: 0
        border-top-right-radius: 0

    .ct-row--scroll .ct-cell--custom-cascade:first-child
        border-left: 0

.ct-loading-overlay--panels
    z-index: 6

.ct-header--custom
    .ct-header__rail
        border-top-left-radius: 0

    .ct-header__cell--channels
        border-top-left-radius: 0

.ct-header__cell--status
    display: flex
    align-items: center
    justify-content: flex-start
    flex: 1 1 220px
    min-width: 200px
    max-width: 240px
    height: 48px
    padding: 16px
    border-bottom: 1px solid #EAEBEE
    border-top: 1px solid $border-new
    text-align: left
    span
        font-size: 11px
        font-weight: 400
        color: #2A3B51
        line-height: normal

.ct-custom-panel
    border: 1px solid $border-new
    border-bottom: 0
    border-radius: 12px 12px 0 0
    background: #FAFBFD
    overflow: hidden
    transition: border-radius 0.28s ease, border-color 0.28s ease

    &--collapsed
        border-bottom: 1px solid $border-new
        border-radius: 12px

.ct-table-content-wrap--collapsible
    display: grid
    grid-template-rows: 0fr
    transition: grid-template-rows 0.28s ease

    &.ct-table-content-wrap--open
        grid-template-rows: 1fr

    .ct-table-content-wrap__inner
        overflow: hidden
        min-height: 0
        opacity: 0
        transition: opacity 0.22s ease
        pointer-events: none

    &.ct-table-content-wrap--open .ct-table-content-wrap__inner
        opacity: 1
        pointer-events: auto

.ct-custom-panel__hdr
    width: 100%
    display: flex
    align-items: center
    gap: 10px
    padding: 12px 14px
    border: 0
    background: transparent
    cursor: pointer

.ct-custom-panel__chevron
    display: inline-flex
    align-items: center
    justify-content: center
    transition: transform 0.28s ease

    &--open
        transform: rotate(180deg)

.ct-custom-panel__title
    color: #2A3B51
    font-size: 14px
    font-weight: 700

.ct-custom-panel__meta
    color: #AAB1B9
    font-size: 12px

.ct-custom-panel__add
    margin-left: auto
    display: inline-flex
    align-items: center
    justify-content: center
    width: 32px
    height: 32px
    border: 1px solid $main-new
    border-radius: 10px
    background: #EEF0FF
    cursor: pointer

    &:disabled
        opacity: .55
        cursor: default

.ct-custom-empty-column
    position: absolute
    inset: 0
    z-index: 1
    display: flex
    align-items: center
    justify-content: center
    padding: 16px 12px
    text-align: center
    color: #7F8997
    font-size: 13px
    line-height: 1.45
    pointer-events: none

.ct-body--custom-scroll-placeholder
    position: relative

.ct-body--custom-scroll-empty
    .ct-row--scroll .ct-cell--custom-cascade-empty
        border-bottom: 0
        background: #fff

.ct-header__cell--custom-cascade-empty
    background: #f8f9f9

.ct-header__cell--custom-cascade
    min-width: 180px

.ct-custom-cascade-head
    display: flex
    flex-direction: column
    align-items: center
    gap: 4px

.ct-custom-cascade-head__num
    font-size: 11px
    font-weight: 700
    color: #2A3B51
    text-transform: uppercase

.ct-custom-cascade-head__name-row
    display: flex
    align-items: center
    justify-content: center
    gap: 3px
    min-width: 0
    width: 100%

.ct-custom-cascade-head__name
    max-width: 88px
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap
    font-size: 10px
    color: #7F8997
    outline: none
    border-radius: 4px
    padding: 1px 3px
    cursor: text

    &[contenteditable="true"]
        background: #EEF0FF
        color: $main-new
        caret-color: $main-new
        white-space: normal
        overflow: visible
        max-width: none

    &--saving
        opacity: .65
        pointer-events: none
        cursor: default

.ct-custom-cascade-head__save-spinner
    flex-shrink: 0

.ct-custom-cascade-head__btn
    display: inline-flex
    align-items: center
    justify-content: center
    width: 18px
    height: 18px
    padding: 0
    border: 0
    border-radius: 5px
    background: transparent
    color: #AAB1B9
    cursor: pointer

    &:hover:not(:disabled)
        background: #EEF0FF
        color: $main-new

    &:disabled
        opacity: .55
        cursor: default

.ct-custom-cascade-head__btn--rename
    &:hover:not(:disabled)
        background: #EEF0FF
        color: $main-new

.ct-custom-cascade-head__btn--danger
    &:hover:not(:disabled)
        background: #FDEDEC
        color: #D14343

.ct-delete-overlay
    position: fixed
    inset: 0
    z-index: 1100
    display: flex
    align-items: center
    justify-content: center
    padding: 20px
    background: rgba(26, 26, 46, 0.45)

.ct-delete-modal
    width: 100%
    max-width: 440px
    padding: 28px
    background: #fff
    border-radius: 16px
    box-shadow: 0 12px 48px rgba(26, 26, 46, 0.18)

.ct-delete-modal__title
    margin-bottom: 10px
    font-family: Geologica, Inter, system-ui, sans-serif
    font-size: 17px
    font-weight: 900
    color: $dark-new

.ct-delete-modal__body
    font-size: 13.5px
    line-height: 1.55
    color: $muted-new

    strong
        font-weight: 700
        color: $dark-new

.ct-delete-modal__actions
    display: flex
    justify-content: flex-end
    gap: 9px
    margin-top: 22px

.ct-delete-modal__btn
    padding: 10px 18px
    border-radius: 10px
    font-family: Geologica, Inter, system-ui, sans-serif
    font-size: 13px
    font-weight: 700
    cursor: pointer

    &:disabled
        opacity: 0.55
        cursor: not-allowed

.ct-delete-modal__btn--ghost
    color: $text-color-new
    background: #fff
    border: 1.5px solid $border-new

    &:hover:not(:disabled)
        border-color: $dark-new

.ct-delete-modal__btn--danger
    color: #E0473A
    background: transparent
    border: 1.5px solid rgba(224, 71, 58, 0.3)

    &:hover:not(:disabled)
        background: #FDEDEC
        border-color: #E0473A

.ct-delete-fade-enter-active,
.ct-delete-fade-leave-active
    transition: opacity 0.2s ease

// ≤620px: only «Каналы» + «Статус» (priority / custom cascade columns hidden)
@media (max-width: 620px)
    .channels-table
        .ct-header__cell--priority,
        .ct-cell--priority
            display: none !important

        .ct-header__cell--channels,
        .ct-cell--channel
            flex: 1 1 0
            width: auto
            min-width: 0
            max-width: none

        .ct-header__cell--status,
        .ct-cell--auth
            flex: 1 1 0
            width: auto
            min-width: 0
            max-width: none
            border-right: 1px solid $border-new

        .ct-header__cell--status
            border-top-right-radius: 12px

        .ct-buy-strip__sub
            display: none

        .ct-buy-strip__content
            flex-wrap: wrap
            gap: 10px

        .ct-buy-strip__actions
            width: 100%
            justify-content: flex-start

    .channels-table--custom
        --ct-custom-channel-w: min(160px, 48vw)
        --ct-custom-status-w: min(180px, 52vw)
        --ct-custom-fixed-w: calc(var(--ct-custom-rail-w) + var(--ct-custom-channel-w) + var(--ct-custom-status-w))

        .ct-custom-panel__add
            display: none !important

        .ct-table-content--custom-split
            .ct-custom-panels
                flex-wrap: wrap

            .ct-custom-panels__fixed
                flex: 1 1 100%
                width: 100%
                max-width: 100%

                .ct-body::after
                    display: none

            // Cascade columns live in the scroll pane — hide them, keep buy actions
            .ct-custom-panels__scroll-scroller
                flex: 1 1 100%
                width: 100%
                max-width: 100%
                overflow: visible
                border-right: 0
                order: 2

                .ct-header--custom-scroll,
                .ct-body--custom-scroll
                    display: none !important

                .ct-custom-panels__scroll-wide
                    width: 100%
                    min-width: 0

            .ct-buy-strip-part--fixed
                width: 100%
                max-width: 100%
                background-size: 100% 100%
                border-radius: 0
                border-bottom: 0
                border-right: 1px solid $border-new

                .ct-buy-strip__content--fixed
                    padding-right: 16px
                    padding-bottom: 4px

                .ct-buy-strip__rail
                    border-bottom-left-radius: 0

            .ct-buy-strip-part--scroll
                position: static
                display: flex
                align-items: stretch
                width: 100%
                max-width: 100%
                border-radius: 0 0 12px 12px
                border-right: 1px solid $border-new
                border-bottom: 1px solid $border-new
                background: linear-gradient(90deg, #FBFEEF, #FFFFFF)

                // Продолжение rail «ДОПОЛНИТЕЛЬНЫЙ» на высоту кнопок
                &::before
                    content: ''
                    width: var(--ct-custom-rail-w, 16px)
                    min-width: var(--ct-custom-rail-w, 16px)
                    flex-shrink: 0
                    background: #EBECF0
                    border-right: 2px solid rgba(170, 178, 196, .4)
                    border-bottom-left-radius: 12px
                    box-sizing: border-box

                .ct-buy-strip__content--scroll
                    flex: 1
                    min-width: 0
                    justify-content: flex-start
                    padding: 4px 16px 10px 20px
                    border-radius: 0 0 12px 0
                    background: transparent

            .ct-group__rows--tariff,
            .ct-group__rows--no-tariff,
            .ct-group__rows--additional
                flex: 1 1 auto
                width: 100%
                min-width: 0

            .ct-row--custom
                width: 100%

            .ct-header__cell--channels,
            .ct-cell--channel
                flex: 1 1 0
                width: auto
                min-width: 0
                max-width: none

            .ct-header__cell--status,
            .ct-cell--auth
                flex: 1 1 0
                width: auto
                min-width: 0
                max-width: none
                border-right: 1px solid $border-new

            .ct-header__cell--status
                border-top-right-radius: 12px

</style>
